import React, { useState, useEffect, useRef, useMemo } from 'react';
import { PaperAirplaneIcon, FaceSmileIcon, BriefcaseIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Switch from '@radix-ui/react-switch';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import {
	getCampaigns,
	getCampaignDialogues,
	getPositionDialogues,
	getCampaignCandidates,
	getDialogueMessages,
	sendDialogueMessage,
	toggleDialogue,
	updateCandidateStatus
} from '../../services/aiRecruiterService';
import type {
	Campaign,
	Dialogue,
	CandidateStatus,
	CandidateStatusFilter,
	GetDialoguesParams,
	CandidateSourceFilter,
	DialogBusinessStatus,
	DialogLifecycleStatus,
} from '../../types/aiRecruiter';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { DialogFiltersBar, emptyDialogFilters, DialogFiltersState } from './DialogFiltersBar';
import { useVacancyStore } from '../../store/vacancyStore';
import { ReactComponent as ExternalLinkIcon } from '../../assets/icons/external-link.svg';
import { ReactComponent as ResumeIcon } from '../../assets/icons/icon-resume.svg';
import { ReactComponent as InterviewIcon } from '../../assets/icons/icon-interview.svg';
import { ReactComponent as AiScreeningIcon } from '../../assets/icons/icon-ai-screening.svg';
import { ReactComponent as AiApprovedBadgeIcon } from '../../assets/icons/ai-approved-badge.svg';
import RelevanceBlock from './RelevanceBlock';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useAuthStore } from '../../store/authStore';
import { selectInitialDialog } from './utils/selectInitialDialog';
import { resolveDialogBadge } from './utils/dialogBadge';
import {
	resolveManualDecisionCandidateStatus,
	type ManualCandidateDecisionStatus,
} from './utils/manualCandidateDecision';
import {
	CANDIDATE_STATUS_BADGE_CLASS,
	CANDIDATE_STATUS_LABEL,
} from './utils/candidateStatusFilter';
import {
	DIALOGUES_PAGE_SIZE,
	getDialoguesPaginationView,
	getDialoguesRefreshRequest,
} from './utils/dialoguesPaging';
import EStaffResumeModal from '../../relevanter/components/EStaffResumeModal';

// TASKNEIROKLYUCH-754: маппинги источника кандидата вынесены на уровень модуля,
// чтобы badge в списке диалогов слева и блок «Источник» в правой панели брали
// одни и те же значения и не расходились.
const SOURCE_LABEL: Record<CandidateSourceFilter, string> = {
	HH_RESPONSE: 'Отклик',
	HH_SEARCH: 'Поиск hh',
	ESTAFF: 'E-Staff',
	AVITO: 'Авито',
	AVITO_RESPONSE: 'Отклик Авито',
	AVITO_SEARCH: 'Поиск Авито',
};
const SOURCE_CLASS: Record<CandidateSourceFilter, string> = {
	HH_RESPONSE: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
	HH_SEARCH: 'bg-blue-50 text-blue-700 border border-blue-200',
	ESTAFF: 'bg-violet-50 text-violet-700 border border-violet-200',
	AVITO: 'bg-green-50 text-green-700 border border-green-200',
	AVITO_RESPONSE: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
	AVITO_SEARCH: 'bg-green-50 text-green-700 border border-green-200',
};

const CAMPAIGN_DIALOGUES_PAGE_SIZE = 100;
const CAMPAIGN_DIALOGUES_MAX_PAGES = 1000;
const SELECTED_DIALOG_MESSAGES_REFRESH_MS = 10000;
const SELECTED_VACANCY_DIALOGUES_REFRESH_MS = 60000;
const ALL_CAMPAIGNS_DIALOGUES_REFRESH_MS = 7 * 24 * 60 * 60 * 1000;

interface DialogsTabProps {
	selectedCampaign: string | null;
	setSelectedCampaign: (campaignId: string | null) => void;
	/**
	 * Deep-link ID диалога из URL (?dialogId=...). Если задан и найден в загруженных
	 * диалогах — выбирается вместо первого по умолчанию (TASKNEIROKLYUCH-647 —
	 * уведомления теперь ведут прямо в диалог).
	 */
	requestedDialogId?: string | null;
}

interface DialogUI {
	id: string | number;
	candidateId?: string | number | null;
	candidateRowId?: string | number | null;
	campaignId: string | number;
	positionId?: number | null;
	positionSlug?: string | null;
	campaignName?: string;
	name: string;
	lastMessage: string;
	_searchPreview?: string;
	lastMessageAt: string;
	lastCandidateMessageAt?: string | null;
	time: string;
	unread: boolean;
	avatar: string;
	status: string;
	candidateStatus?: CandidateStatusFilter | null;
	rawCandidateStatus?: CandidateStatus | null;
	skipReason?: string | null;
	errorCode?: string | null;
	lifecycleStatus?: DialogLifecycleStatus | null;
	manualMode?: boolean | null;
	personalDataConsent?: boolean | null;
	resumeUrl?: string | null;
	resumeId?: string | number | null;
	hhResumeId?: string | null;
	externalId?: string | null;
	vacancy?: string;
	registrationToken?: string | null;
	messengerType?: string | null;
	screeningStatus?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | null;
	screeningResult?: 'PASSED' | 'FAILED' | 'PARTIALLY' | null;
	screeningScore?: number | null;
	screeningLinkVisitedAt?: string | null;
	screeningError?: string | null;
	approvalSource?: 'AI_AUTO' | 'MANUAL' | null;
	isAutoApproved?: boolean;
	// Снапшот оценки релевантности на момент создания рассылки
	relevanceScore?: number | null;
	relevanceFeedback?: string | null;
	relevanceMatchLevel?: string | null;
	// Источник кандидата: HH_RESPONSE | HH_SEARCH | ESTAFF | AVITO_RESPONSE | AVITO_SEARCH
	source?: CandidateSourceFilter | null;
	// TASKNEIROKLYUCH-737: сквозной статус, рассчитанный на бэке.
	dialogStatus?: DialogBusinessStatus | null;
}

export type DialogResumeLinkData = {
	resumeUrl?: string | null;
	resumeId?: string | number | null;
	hhResumeId?: string | null;
	externalId?: string | null;
	candidateId?: string | number | null;
	personalDataConsent?: boolean | null;
	messengerType?: string | null;
	source?: CandidateSourceFilter | null;
};

export type DialogEStaffResumeData = {
	id: string;
	source: 'estaff';
};

export type DialogCandidateStatusActionData = {
	candidateId?: string | number | null;
	candidateRowId?: string | number | null;
	campaignId?: string | number | null;
};

const normalizeResumeId = (value: unknown): string | null => {
	if (value == null) return null;
	if (typeof value === 'string') return value.trim() ? value.trim() : null;
	if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
	return null;
};

const getHhResumeUrl = (resumeId: unknown) => {
	const id = normalizeResumeId(resumeId);
	return id ? `https://hh.ru/resume/${id}` : null;
};

const isHhResumeSource = (dialog: DialogResumeLinkData) => {
	return dialog.messengerType === 'HH' || dialog.source === 'HH_SEARCH' || dialog.source === 'HH_RESPONSE';
};

const isAvitoResumeSource = (dialog: DialogResumeLinkData) => {
	return dialog.messengerType === 'AVITO'
		|| dialog.source === 'AVITO'
		|| dialog.source === 'AVITO_RESPONSE'
		|| dialog.source === 'AVITO_SEARCH';
};

export const resolveDialogResumeLink = (dialog: DialogResumeLinkData | null | undefined) => {
	if (!dialog) return null;
	const isHh = isHhResumeSource(dialog);
	if (dialog.resumeUrl) return dialog.resumeUrl;
	if (!isHh) return null;
	return getHhResumeUrl(dialog.hhResumeId ?? dialog.resumeId ?? dialog.externalId);
};

export const getDialogResumeLinkLabel = (dialog: DialogResumeLinkData | null | undefined) => {
	if (dialog && isAvitoResumeSource(dialog)) return 'Резюме на Авито';
	if (dialog && isHhResumeSource(dialog)) return 'Резюме на hh.ru';
	return 'Резюме';
};

export const resolveDialogEStaffResumeData = (dialog: DialogResumeLinkData | null | undefined): DialogEStaffResumeData | null => {
	if (!dialog || String(dialog.source || '').toUpperCase() !== 'ESTAFF') return null;
	const id = [dialog.externalId, dialog.resumeId, dialog.candidateId]
		.map(normalizeResumeId)
		.find((value): value is string => Boolean(value));
	return id ? { id, source: 'estaff' } : null;
};

export const resolveDialogCandidateStatusActionId = (
	dialog: DialogCandidateStatusActionData | null | undefined
): string | null => {
	return [dialog?.candidateRowId, dialog?.candidateId]
		.map(normalizeResumeId)
		.find((value): value is string => Boolean(value)) ?? null;
};

export const canUpdateDialogCandidateStatus = (
	dialog: DialogCandidateStatusActionData | null | undefined
): dialog is DialogCandidateStatusActionData & { campaignId: string | number } => {
	return Boolean(resolveDialogCandidateStatusActionId(dialog) && normalizeResumeId(dialog?.campaignId));
};

const shouldHideDialogResumeFields = (dialog: DialogResumeLinkData | null | undefined) => {
	return Boolean(dialog && isHhResumeSource(dialog) && dialog.personalDataConsent !== true);
};

const resolveSafeDialogResumeFields = (dialog: DialogResumeLinkData) => {
	if (shouldHideDialogResumeFields(dialog)) {
		return {
			resumeUrl: resolveDialogResumeLink(dialog),
			resumeId: null,
			hhResumeId: null,
			externalId: null,
		};
	}
	return {
		resumeUrl: resolveDialogResumeLink(dialog),
		resumeId: dialog.resumeId ?? null,
		hhResumeId: dialog.hhResumeId ?? null,
		externalId: dialog.externalId ?? null,
	};
};

interface MessageUI {
	id: string | number;
	sender: string;
	text: string;
	time: string;
	isBot: boolean;
}

// Маппинг hhErrorValue (см. ErrorsVacancyApplyForbiddenError в HH openapi.yml,
// также реплицировано в relevanter-back routes/hh.js) на текст для рекрутера.
// Без этого пользователь видит generic "Не удалось отправить" и идёт переавторизовываться
// зря, когда реальная причина — состояние диалога/вакансии на стороне HH.
const HH_ERROR_VALUE_LABELS: Record<string, string> = {
	invalid_vacancy: 'HH временно не принимает сообщения для этой вакансии. Попробуйте ещё раз через минуту.',
	archived: 'Вакансия в архиве HH — отправка сообщений недоступна.',
	chat_archived: 'Чат с кандидатом в HH архивирован — отправка сообщений недоступна.',
	topic_archived: 'Отклик/приглашение в HH архивирован — отправка сообщений недоступна.',
	in_a_row_limit: 'HH: слишком много сообщений подряд. Дождитесь ответа кандидата.',
	overall_limit: 'HH: превышен лимит сообщений в этом чате.',
	disabled_by_employer: 'HH: переписка по отклику отключена работодателем.',
	no_invitation: 'HH: переписка недоступна — кандидат ещё не приглашён.',
	chat_is_not_ready: 'Чат HH ещё не создан, попробуйте через минуту.',
	resume_deleted: 'Резюме кандидата на HH удалено или скрыто.',
	resume_not_found: 'Резюме кандидата на HH не найдено.',
	wrong_state: 'HH: действие в текущем статусе отклика невозможно.',
	empty_message: 'HH: сообщение не может быть пустым.',
	too_long_message: 'HH: сообщение слишком длинное.',
	inappropriate_language_message: 'HH отклонил сообщение: нецензурная лексика.',
	resource_policy_violation_message: 'HH отклонил сообщение: нарушение правил сервиса.',
	too_many_requests: 'HH временно ограничил отправку сообщений. Попробуйте повторить позже.',
	bad_authorization: 'Авторизация в HH истекла. Обновите токен HH и попробуйте снова.',
	token_expired: 'Токен HH истёк. Обновите токен в настройках интеграции.',
};

/**
 * Извлекает причину отправки сообщения из axios-ошибки, маппит hhErrorValue
 * на русский текст. Backend (ai-recruiter-back, relevanter-back) кладёт hhErrorValue
 * в response body — здесь только UI-маппинг. Когда hhErrorValue нет (например,
 * сетевой сбой) — отдаём текст ошибки от backend или generic-фразу.
 *
 * Для статусов 401 и hhErrorType=oauth — отдельный текст про переавторизацию
 * (раньше handleError в relevanter маскировал ЛЮБОЙ 403 под auth, что было
 * ложным сигналом для invalid_vacancy; теперь обратная задача — реально-auth
 * ошибки должны явно говорить «обновите токен»).
 */
export const formatSendMessageError = (error: unknown): string => {
	const err = error as {
		response?: {
			status?: number;
			data?: { error?: string; hhErrorValue?: string; hhErrorType?: string; hhMessage?: string };
		};
	};
	const data = err?.response?.data;
	const status = err?.response?.status;

	if (data?.hhErrorValue && HH_ERROR_VALUE_LABELS[data.hhErrorValue]) {
		return HH_ERROR_VALUE_LABELS[data.hhErrorValue];
	}
	if (status === 401 || data?.hhErrorType === 'oauth') {
		return 'Авторизация в HH истекла. Обновите токен HH и попробуйте снова.';
	}
	if (data?.hhErrorValue) {
		return `Ошибка HH: ${data.hhErrorValue}`;
	}
	if (data?.hhMessage) {
		return `Ошибка HH: ${data.hhMessage}`;
	}
	if (data?.error) {
		return data.error;
	}
	return 'Не удалось отправить сообщение';
};

export const isDialogueAiStopped = (
	dialog: { status?: string | null; lifecycleStatus?: DialogLifecycleStatus | null; manualMode?: boolean | null } | null | undefined
): boolean => {
	if (!dialog) return false;
	return Boolean(dialog.manualMode) || dialog.status === 'PAUSED' || dialog.status === 'CLOSED_INACTIVE';
};

const isDialogueLifecycleStopped = (
	dialog: { status?: string | null; lifecycleStatus?: DialogLifecycleStatus | null } | null | undefined
): boolean => {
	if (!dialog) return false;
	return dialog.lifecycleStatus === 'STOPPED'
		|| dialog.status === 'CLOSED'
		|| dialog.status === 'CLOSED_INACTIVE';
};

const DialogsTab: React.FC<DialogsTabProps> = ({ selectedCampaign, setSelectedCampaign, requestedDialogId }) => {
	const { selectedVacancyId, selectedVacancy } = useVacancyStore();
	const [selectedDialog, setSelectedDialog] = useState<DialogUI | null>(null);
	const [message, setMessage] = useState('');
	const [isAiStopped, setIsAiStopped] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const messagesContainerRef = useRef<HTMLDivElement | null>(null);
	const [campaigns, setCampaigns] = useState<Campaign[]>([]);
	const [dialogs, setDialogs] = useState<DialogUI[]>([]);
	const [dialoguesPage, setDialoguesPage] = useState(0);
	const [dialoguesPageSize, setDialoguesPageSize] = useState(DIALOGUES_PAGE_SIZE);
	const [dialoguesTotalPages, setDialoguesTotalPages] = useState(0);
	const [dialoguesTotalElements, setDialoguesTotalElements] = useState(0);
	const [messages, setMessages] = useState<MessageUI[]>([]);
	const [loading, setLoading] = useState(false);
	const [dialogsLoading, setDialogsLoading] = useState(false);
	const [messagesLoading, setMessagesLoading] = useState(false);
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const emojiPickerRef = useRef<HTMLDivElement | null>(null);
	const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
	const colors = useBrandingColors();
	const [isNameHover, setIsNameHover] = useState(false);
	const [copiedLinkKey, setCopiedLinkKey] = useState<string | null>(null);
	const [estaffResumeModalOpen, setEstaffResumeModalOpen] = useState(false);
	const [selectedEStaffResume, setSelectedEStaffResume] = useState<DialogEStaffResumeData | null>(null);
	const [hasAiScreeningQuestions, setHasAiScreeningQuestions] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [searchLoading, setSearchLoading] = useState(false);
	const [filters, setFilters] = useState<DialogFiltersState>(emptyDialogFilters);
	// Ref нужен, чтобы refreshDialogues и другие замыкания,
	// созданные на момент первого рендера, всегда брали актуальные фильтры.
	const filtersRef = useRef<DialogFiltersState>(emptyDialogFilters);
	const dialogsRef = useRef<DialogUI[]>([]);
	const dialoguesPageRef = useRef(0);
	const dialoguesPageSizeRef = useRef(DIALOGUES_PAGE_SIZE);
	const dialogsLengthRef = useRef(0);
	const debouncedSearchRef = useRef('');
	const loadingNextDialoguesPageRef = useRef(false);
	const dialoguesRequestSeqRef = useRef(0);
	const dialogsLoadingRef = useRef(false);
	const refreshingDialoguesRef = useRef(false);
	const selectedDialogRef = useRef<DialogUI | null>(null);
	const handledRequestedDialogIdRef = useRef<string | null>(null);
	const messagesLoadRequestSeqRef = useRef(0);
	const messagesRefreshRequestSeqRef = useRef(0);
	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);
	useEffect(() => {
		dialogsRef.current = dialogs;
		dialoguesPageRef.current = dialoguesPage;
		dialoguesPageSizeRef.current = dialoguesPageSize;
		dialogsLengthRef.current = dialogs.length;
		debouncedSearchRef.current = debouncedSearch;
	}, [dialogs, dialoguesPage, dialoguesPageSize, debouncedSearch]);
	useEffect(() => {
		selectedDialogRef.current = selectedDialog;
	}, [selectedDialog]);
	useEffect(() => {
		handledRequestedDialogIdRef.current = null;
	}, [requestedDialogId]);
	useEffect(() => {
		if (!requestedDialogId) return;
		const requestedDialogKey = String(requestedDialogId);
		if (handledRequestedDialogIdRef.current === requestedDialogKey) return;

		const found = dialogs.find((dialog) => String(dialog.id) === requestedDialogKey);
		if (!found) return;

		handledRequestedDialogIdRef.current = requestedDialogKey;
		if (String(selectedDialogRef.current?.id) !== requestedDialogKey) {
			setSelectedDialog(found);
		}
	}, [requestedDialogId, dialogs]);
	useEffect(() => {
		messagesLoadRequestSeqRef.current += 1;
		messagesRefreshRequestSeqRef.current += 1;
	}, [selectedDialog?.id]);
	// When true, after messages load — find first message matching searchQuery and scroll to it
	const [shouldScrollToMatch, setShouldScrollToMatch] = useState(false);
	const [isCompactDialogsList, setIsCompactDialogsList] = useState(false);
	const [isAdaptivePanels, setIsAdaptivePanels] = useState(false);
	const [isShortViewport, setIsShortViewport] = useState(false);
	const [isVeryShortViewport, setIsVeryShortViewport] = useState(false);
	const MESSAGE_INPUT_MAX_HEIGHT = 160;

	const getPendingRequestedDialogId = (): string | null => {
		if (!requestedDialogId) return null;
		const requestedDialogKey = String(requestedDialogId);
		return handledRequestedDialogIdRef.current === requestedDialogKey ? null : requestedDialogId;
	};

	const markRequestedDialogHandled = (dialog: DialogUI | null) => {
		if (!requestedDialogId || !dialog) return;
		const requestedDialogKey = String(requestedDialogId);
		if (String(dialog.id) === requestedDialogKey) {
			handledRequestedDialogIdRef.current = requestedDialogKey;
		}
	};

	useEffect(() => {
		if (!selectedVacancyId) {
			setHasAiScreeningQuestions(false);
			return;
		}
		apiService.getQuestions(selectedVacancyId)
			.then(data => setHasAiScreeningQuestions((data?.questions?.length ?? 0) > 0))
			.catch(() => setHasAiScreeningQuestions(false));
	}, [selectedVacancyId]);

	useEffect(() => {
		const updateViewportState = () => {
			setIsCompactDialogsList(window.innerWidth <= 1026);
			setIsAdaptivePanels(window.innerHeight <= 860);
			setIsShortViewport(window.innerHeight <= 790);
			setIsVeryShortViewport(window.innerHeight <= 720);
		};

		updateViewportState();
		window.addEventListener('resize', updateViewportState);

		return () => {
			window.removeEventListener('resize', updateViewportState);
		};
	}, []);

	// Normalize Latin/Cyrillic homoglyphs for search (a→а, o→о, e→е, etc.)
	const normalizeHomoglyphs = (str: string): string => {
		const map: Record<string, string> = {
			'a': 'а', 'A': 'А', 'e': 'е', 'E': 'Е', 'o': 'о', 'O': 'О',
			'p': 'р', 'P': 'Р', 'c': 'с', 'C': 'С', 'x': 'х', 'X': 'Х',
			'y': 'у', 'T': 'Т', 'H': 'Н', 'B': 'В', 'M': 'М', 'K': 'К',
		};
		return str.replace(/./g, ch => map[ch] || ch);
	};

	const sortDialogs = (list: DialogUI[]) => {
		// TASKNEIROKLYUCH-690: PROBLEM ("требует внимания") наверх, потом lastMessageAt DESC.
		//
		// Дублирует серверную PROBLEM-first сортировку (PositionDialoguesController/CampaignController)
		// намеренно: бэк гарантирует порядок только в рамках одной страницы, а фронт агрегирует
		// несколько страниц через append/load-more (а также merge-by-id при обновлениях) —
		// после слияния PROBLEM из page 2 могут оказаться ниже non-PROBLEM из page 1 если не пересортировать.
		return [...list].sort((a, b) => {
			const aProblem = a.dialogStatus === 'PROBLEM';
			const bProblem = b.dialogStatus === 'PROBLEM';
			if (aProblem !== bProblem) return aProblem ? -1 : 1;
			const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
			const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
			return timeB - timeA;
		});
	};

	const getCampaignCandidateKey = (campaignId: string | number, candidateId: string | number) =>
		`${campaignId}\u0000${candidateId}`;

	const loadAllCampaignDialoguePages = async (
		campaign: Campaign,
		params: GetDialoguesParams,
		config?: any,
	): Promise<Dialogue[]> => {
		const result: Dialogue[] = [];
		const seenIds = new Set<string>();
		let page = 0;
		let totalPages = 1;

		while (page < totalPages && page < CAMPAIGN_DIALOGUES_MAX_PAGES) {
			const data = await getCampaignDialogues(
				campaign.id,
				{
					...params,
					page,
					size: CAMPAIGN_DIALOGUES_PAGE_SIZE,
				},
				config,
			);
			const list = data.content || [];
			let addedOnPage = 0;

			list.forEach(d => {
				const id = String(d.id);
				if (seenIds.has(id)) return;
				seenIds.add(id);
				addedOnPage += 1;
				result.push({
					...d,
					campaignId: campaign.id,
					positionId: d.positionId ?? campaign.positionId ?? null,
					positionSlug: d.positionSlug ?? campaign.positionSlug ?? null,
					campaignName: campaign.name,
					// TASKNEIROKLYUCH-728: пробрасываем targetPosition, чтобы в сайдбаре
					// показывать название вакансии, а не рассылки.
					vacancyTitle: campaign.targetPosition,
				});
			});

			if (typeof data.totalPages === 'number') {
				totalPages = data.totalPages;
			} else if (list.length < CAMPAIGN_DIALOGUES_PAGE_SIZE || addedOnPage === 0) {
				break;
			} else {
				totalPages = page + 2;
			}
			page += 1;
		}

		return result;
	};

	// Debounce search query for server-side message search
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchQuery.trim());
		}, 500);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Server-side deep search using existing dialogues endpoint with search param.
	// Returns additional dialogs not found by frontend filter (e.g. by message content, by full name in DB).
	const [serverDialogs, setServerDialogs] = useState<DialogUI[]>([]);
	useEffect(() => {
		if (selectedVacancyId) {
			setServerDialogs([]);
			setSearchLoading(false);
			return;
		}
		if (!debouncedSearch) {
			setServerDialogs([]);
			setSearchLoading(false);
			return;
		}
		if (!selectedVacancyId && campaigns.length === 0) return;

		let cancelled = false;
		setSearchLoading(true);

		const config = undefined;
		const searchFilterParams = toDialoguesParams(filtersRef.current);

		(async () => {
			try {
				let allServerDialogues: Dialogue[] = [];
				for (const campaign of campaigns) {
					try {
						const list = await loadAllCampaignDialoguePages(campaign, {
							search: debouncedSearch,
							...searchFilterParams,
						}, config);
						allServerDialogues = [...allServerDialogues, ...list];
					} catch (err) {
						console.error(`Server search failed for campaign ${campaign.id}:`, err);
					}
				}
				if (!cancelled) {
					// Transform to DialogUI format
					const transformed: DialogUI[] = allServerDialogues.map((d, index) => {
						const dialogData: any = d;
						const externalId = normalizeResumeId(dialogData.externalId ?? dialogData.external_id);
						const hhResumeId = normalizeResumeId(dialogData.hhResumeId ?? dialogData.hh_resume_id ?? externalId);
						const resumeId = normalizeResumeId(dialogData.resumeId ?? dialogData.resume_id ?? externalId);
						const personalDataConsent = dialogData.personalDataConsent === true;
						const resumeFields = resolveSafeDialogResumeFields({
							resumeUrl: d.resumeUrl ?? dialogData.resumeUrl ?? dialogData.resume_url ?? null,
							messengerType: d.messengerType ?? null,
							source: dialogData.source ?? null,
							hhResumeId,
							resumeId,
							externalId,
							personalDataConsent,
						});
						return {
							id: d.id,
							candidateId: d.candidateId,
							candidateRowId: dialogData.candidateRowId ?? dialogData.candidate_row_id ?? null,
							campaignId: d.campaignId,
							campaignName: d.campaignName,
							name: d.candidateName || `Кандидат ${d.candidateId || index + 1}`,
							lastMessage: d.lastMessage || 'Диалог начат',
							lastMessageAt: d.lastMessageAt || d.createdAt || '',
							lastCandidateMessageAt: d.lastCandidateMessageAt ?? null,
							time: formatTime(d.lastMessageAt || d.createdAt || ''),
							unread: false,
							avatar: getInitials(d.candidateName || `Кандидат ${d.candidateId || index + 1}`),
							status: d.status,
							lifecycleStatus: dialogData.lifecycleStatus ?? null,
							manualMode: Boolean(dialogData.manualMode),
							candidateStatus: d.candidateStatus,
							rawCandidateStatus: d.rawCandidateStatus ?? null,
							skipReason: dialogData.skipReason ?? null,
							errorCode: dialogData.errorCode ?? null,
							personalDataConsent,
							...resumeFields,
							positionId: d.positionId ?? null,
							positionSlug: d.positionSlug ?? null,
							registrationToken: dialogData.registrationToken ?? null,
							messengerType: d.messengerType || null,
							screeningStatus: dialogData.screeningStatus || null,
							screeningResult: dialogData.screeningResult || null,
							screeningScore: dialogData.screeningScore || null,
							screeningLinkVisitedAt: dialogData.screeningLinkVisitedAt || null,
							screeningError: dialogData.screeningError || null,
							approvalSource: dialogData.approvalSource || null,
							isAutoApproved: Boolean(dialogData.isAutoApproved),
							relevanceScore: dialogData.relevanceScore ?? null,
							relevanceFeedback: dialogData.relevanceFeedback ?? null,
							relevanceMatchLevel: dialogData.relevanceMatchLevel ?? null,
							source: dialogData.source ?? null,
							dialogStatus: dialogData.dialogStatus ?? null,
							_serverSearchMatch: true
						} as DialogUI & { _serverSearchMatch?: boolean };
					});
					setServerDialogs(transformed);
				}
			} finally {
				if (!cancelled) setSearchLoading(false);
			}
		})();

		return () => { cancelled = true; };
	}, [debouncedSearch, campaigns, selectedVacancyId, filters]);


	// Hybrid filtered dialogs: instant front filter + server search results merged
	const filteredDialogs = useMemo(() => {
		if (selectedVacancyId) return dialogs;
		if (!searchQuery.trim()) return dialogs;
		const q = normalizeHomoglyphs(searchQuery.trim().toLowerCase());

		// Helper: extract snippet around the match for preview
		const getSnippet = (text: string, maxLen = 80): string => {
			const normalized = normalizeHomoglyphs(text.toLowerCase());
			const idx = normalized.indexOf(q);
			if (idx === -1) return text.substring(0, maxLen);
			const start = Math.max(0, idx - 20);
			const end = Math.min(text.length, idx + q.length + 40);
			let snippet = text.substring(start, end).replace(/\n/g, ' ');
			if (start > 0) snippet = '...' + snippet;
			if (end < text.length) snippet = snippet + '...';
			return snippet;
		};

		// 1. Instant frontend filter on already-loaded dialogs.
		const frontMatches = dialogs.filter(dialog => {
			if (normalizeHomoglyphs(dialog.name.toLowerCase()).includes(q)) return true;
			if (normalizeHomoglyphs(dialog.lastMessage.toLowerCase()).includes(q)) return true;
			if (dialog.campaignName && normalizeHomoglyphs(dialog.campaignName.toLowerCase()).includes(q)) return true;
			return false;
		}).map(dialog => {
			// If match is in lastMessage or name, keep lastMessage as preview
			const nameMatch = normalizeHomoglyphs(dialog.name.toLowerCase()).includes(q);
			const msgMatch = normalizeHomoglyphs(dialog.lastMessage.toLowerCase()).includes(q);
			if (msgMatch) {
				return { ...dialog, _searchPreview: getSnippet(dialog.lastMessage) };
			}
			// Name or campaign match — no special preview needed
			return nameMatch ? dialog : { ...dialog, _searchPreview: dialog.lastMessage };
		});

		// 2. Merge with server results (add dialogs not already in frontMatches).
		const existingIds = new Set(frontMatches.map(d => String(d.id)));
		const extraFromServer = serverDialogs
			.filter(d => !existingIds.has(String(d.id)))
			.map(d => {
				// Server found this by message content — show snippet from lastMessage or mark
				const msgMatch = normalizeHomoglyphs(d.lastMessage.toLowerCase()).includes(q);
				return {
					...d,
					_searchPreview: msgMatch ? getSnippet(d.lastMessage) : '💬 Найдено в сообщениях'
				};
			});

		return [...frontMatches, ...extraFromServer];
	}, [dialogs, searchQuery, serverDialogs, selectedVacancyId]);

	// Когда серверный список диалогов становится пустым (фильтр не дал результатов),
	// синхронизируем правую панель: снимаем выбор и очищаем сообщения.
	// Это страховочный эффект — основная логика уже вызывает setSelectedDialog(null)
	// в loadDialoguesFromAllCampaigns/loadDialoguesByPosition, но гонка между
	// поллингом и загрузкой может восстановить selectedDialog после обнуления.
	useEffect(() => {
		if (dialogs.length === 0) {
			setSelectedDialog(null);
			setMessages([]);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogs.length]);

	// Синхронизируем переключатель с реальным режимом ответа ИИ.
	useEffect(() => {
		setIsAiStopped(isDialogueAiStopped(selectedDialog));
	}, [selectedDialog]);

	const scrollMessagesContainerTo = (top: number, behavior: ScrollBehavior = 'smooth') => {
		const container = messagesContainerRef.current;
		if (!container) return;

		if (typeof container.scrollTo === 'function') {
			container.scrollTo({ top, behavior });
		} else {
			container.scrollTop = top;
		}
	};

	// Function to scroll to bottom of messages
	const scrollToBottom = () => {
		const container = messagesContainerRef.current;
		if (container) {
			scrollMessagesContainerTo(container.scrollHeight, 'smooth');
		}
	};

	// Load campaigns on mount + при смене активной вакансии.
	// Раньше был deps=[] — при deep-link из уведомлений 647 (positionId в URL
	// переключает vacancy АСИНХРОННО после mount) campaigns не перезагружались
	// → DialogsTab тянул с прежним tenant → «Диалоги отсутствуют».
	useEffect(() => {
		loadCampaignsData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVacancyId]);

	// Перезагрузка диалогов при смене фильтров. При первом mount кампании
	// ещё не загружены — этот эффект пропускается, начальную загрузку берёт
	// на себя loadCampaignsData (он сам использует actual фильтры из ref).
	//
	// X-Tenant-Id сюда передавать НЕ надо: selectedVacancyId != companyId,
	// а relevanter-back как SUPER_ADMIN-override пропихнёт vacancyId в ai-recruiter
	// как tenantId → ai-recruiter не найдёт кампанию (у неё tenant_id = companyId).
	// X-Tenant-Id ставит прокси сам из JWT companyId — см. addTenantIdHeader.
	useEffect(() => {
		if (selectedVacancyId) {
			return;
		}
		if (campaigns.length === 0) return;
		loadDialoguesFromAllCampaigns(campaigns);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters]);

	useEffect(() => {
		if (!selectedVacancyId) return;
		loadDialoguesByPosition(selectedVacancyId, undefined, { page: 0, append: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVacancyId, filters, debouncedSearch]);

	// Scroll to matching message (from search) or to bottom
	useEffect(() => {
		// Wait until messages are fully loaded before trying to scroll
		if (messagesLoading) return;

		if (shouldScrollToMatch && messages.length > 0 && searchQuery.trim()) {
			const q = normalizeHomoglyphs(searchQuery.trim().toLowerCase());
			// Find first message containing the search query
			const matchingMsg = messages.find(m =>
				normalizeHomoglyphs(m.text?.toLowerCase() || '').includes(q)
			);
			if (matchingMsg) {
				requestAnimationFrame(() => {
					const container = messagesContainerRef.current;
					const el = container
						? Array.from(container.querySelectorAll<HTMLElement>('[data-message-id]'))
							.find((node) => node.dataset.messageId === String(matchingMsg.id))
						: null;
					if (container && el) {
						const targetTop = el.offsetTop - (container.clientHeight / 2) + (el.clientHeight / 2);
						scrollMessagesContainerTo(Math.max(0, targetTop), 'smooth');
						el.classList.add('ring-2', 'ring-yellow-400', 'rounded-lg');
						setTimeout(() => {
							el.classList.remove('ring-2', 'ring-yellow-400', 'rounded-lg');
						}, 3000);
					}
				});
			} else {
				scrollToBottom();
			}
			setShouldScrollToMatch(false);
		} else if (!shouldScrollToMatch) {
			scrollToBottom();
		}
	}, [messages, messagesLoading, shouldScrollToMatch]);

	useEffect(() => {
		const input = messageInputRef.current;
		const messagesContainer = messagesContainerRef.current;
		if (!input) return;
		const distanceToBottom = messagesContainer
			? messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight
			: Number.POSITIVE_INFINITY;
		const shouldStickToBottom = distanceToBottom < 120;
		input.style.height = '0px';
		const nextHeight = Math.min(input.scrollHeight, MESSAGE_INPUT_MAX_HEIGHT);
		input.style.height = `${nextHeight}px`;
		input.style.overflowY = input.scrollHeight > MESSAGE_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
		if (shouldStickToBottom && messagesContainer) {
			requestAnimationFrame(() => {
				if (messagesContainerRef.current) {
					messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
				}
			});
		}
	}, [message, selectedDialog]);

	// Load messages when dialog is selected
	useEffect(() => {
		setMessages([]);
		if (selectedDialog) {
			loadMessagesData(selectedDialog);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDialog?.id]);

	// Auto-refresh messages for active dialog.
	useEffect(() => {
		if (!selectedDialog?.campaignId) return;

		const intervalId = setInterval(() => {
			// Silently refresh messages without showing loading state
			refreshMessages();
		}, SELECTED_DIALOG_MESSAGES_REFRESH_MS);

		// Cleanup interval on unmount or when dialog changes
		return () => clearInterval(intervalId);
	}, [selectedDialog?.id, selectedDialog?.campaignId]);

	// Auto-refresh dialogues list without blocking the current view.
	useEffect(() => {
		if (!selectedVacancyId && campaigns.length === 0) return;

		const refreshIntervalMs = selectedVacancyId
			? SELECTED_VACANCY_DIALOGUES_REFRESH_MS
			: ALL_CAMPAIGNS_DIALOGUES_REFRESH_MS;
		const intervalId = setInterval(() => {
			// Silently refresh dialogues list
			refreshDialogues();
		}, refreshIntervalMs);

		// Cleanup interval on unmount or when selected vacancy/campaigns change
		return () => clearInterval(intervalId);
	}, [selectedVacancyId, campaigns, dialogs.length, debouncedSearch]);

	// Handle click outside emoji picker to close it
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
				setShowEmojiPicker(false);
			}
		}

		if (showEmojiPicker) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showEmojiPicker]);

	/**
	 * Форматирует Date в строку ISO без таймзоны (YYYY-MM-DDTHH:mm:ss).
	 * Нужен для фильтра по датам: бэк принимает LocalDateTime и не учитывает
	 * смещение, поэтому отправка UTC-времени через toISOString() сдвигала
	 * границы диапазона на TZ-офсет пользователя.
	 */
	const formatLocalIsoDateTime = (d: Date): string => {
		const pad = (n: number) => String(n).padStart(2, '0');
		return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
	};

	/**
	 * Преобразует UI-состояние фильтров в GetDialoguesParams.
	 * Все пустые значения отбрасываются, чтобы не слать лишние query-параметры
	 * (бэк интерпретирует отсутствие параметра как «без фильтра»).
	 */
	const toDialoguesParams = (
		f: DialogFiltersState,
	): Partial<GetDialoguesParams> => {
		const params: Partial<GetDialoguesParams> = {};
		if (f.dialogStatuses.length) params.dialogStatus = f.dialogStatuses;
		if (f.sources.length) params.source = f.sources;
		if (f.messengers.length) params.messenger = f.messengers;
		if (f.lifecycleStatuses.length) {
			params.lifecycleStatus = f.lifecycleStatuses;
		}
		if (f.candidateStatuses.length) params.candidateStatuses = f.candidateStatuses;
		if (f.createdFrom) {
			// Начало выбранного дня в локальной зоне
			const from = new Date(f.createdFrom);
			from.setHours(0, 0, 0, 0);
			params.createdFrom = formatLocalIsoDateTime(from);
		}
		if (f.createdTo) {
			// Конец выбранного дня, чтобы весь последний день попал в диапазон
			const to = new Date(f.createdTo);
			to.setHours(23, 59, 59, 999);
			params.createdTo = formatLocalIsoDateTime(to);
		}
		return params;
	};

	// Загрузка диалогов по всем кампаниям и сортировка по времени
	const loadDialoguesFromAllCampaigns = async (campaignsList: Campaign[], config?: any) => {
		setDialogsLoading(true);
		try {
			let allDialogues: Dialogue[] = [];
			const candidatesMap: Record<string, any> = {};
			// Берём фильтры из ref, чтобы поллинг/авто-refresh всегда использовал
			// актуальное состояние, не stale-значение из замыкания.
			const filterParams = toDialoguesParams(filtersRef.current);

			// Загружаем диалоги и кандидатов по каждой кампании
			for (const campaign of campaignsList) {
				try {
					const dialoguesList = await loadAllCampaignDialoguePages(campaign, filterParams, config);

					// Загружаем кандидатов для получения registrationToken
					try {
						const candidatesData = await getCampaignCandidates(String(campaign.id), config);
						if (Array.isArray(candidatesData)) {
							candidatesData.forEach((c: any) => {
								const candidateActionId = normalizeResumeId(c.candidateRowId ?? c.candidate_row_id ?? c.id ?? c.candidateId);
								const externalCandidateId = normalizeResumeId(c.candidateId);
								if (candidateActionId) {
									candidatesMap[getCampaignCandidateKey(campaign.id, candidateActionId)] = c;
								}
								if (externalCandidateId && externalCandidateId !== candidateActionId) {
									candidatesMap[getCampaignCandidateKey(campaign.id, externalCandidateId)] = c;
								}
							});
						}
					} catch (err) {
						console.error(`Failed to load candidates for campaign ${campaign.id}:`, err);
					}

					allDialogues = [...allDialogues, ...dialoguesList];
				} catch (err) {
					console.error(`Failed to load dialogues for campaign ${campaign.id}:`, err);
				}
			}

			// Трансформируем диалоги для UI
			const transformedDialogues: DialogUI[] = allDialogues.map((d, index) => {
				const dialogData: any = d;
				const dialogueCandidateId = normalizeResumeId(dialogData.candidateRowId ?? dialogData.candidate_row_id ?? d.candidateId);
				const candidate = dialogueCandidateId
					? candidatesMap[getCampaignCandidateKey(d.campaignId, dialogueCandidateId)]
					: undefined;
				const candidateData = candidate as (typeof candidate & {
					external_id?: string | null;
					hh_resume_id?: string | null;
					resume_id?: string | number | null;
					resume_url?: string | null;
				}) | undefined;
				const externalId = normalizeResumeId(
					dialogData.externalId ?? dialogData.external_id ?? candidate?.externalId ?? candidateData?.external_id
				);
				const hhResumeId = normalizeResumeId(
					dialogData.hhResumeId ?? dialogData.hh_resume_id ?? candidate?.hhResumeId ?? candidateData?.hh_resume_id ?? externalId
				);
				const resumeId = normalizeResumeId(
					dialogData.resumeId ?? dialogData.resume_id ?? candidate?.resumeId ?? candidateData?.resume_id ?? externalId
				);
				const personalDataConsent = dialogData.personalDataConsent === true || candidate?.pdnConsent === true;
				const resumeFields = resolveSafeDialogResumeFields({
					resumeUrl: d.resumeUrl ?? dialogData.resumeUrl ?? dialogData.resume_url ?? candidate?.resumeUrl ?? candidateData?.resume_url ?? null,
					messengerType: d.messengerType ?? null,
					source: dialogData.source ?? null,
					hhResumeId,
					resumeId,
					externalId,
					personalDataConsent,
				});
				return {
					id: d.id,
					candidateId: d.candidateId,
					candidateRowId: dialogData.candidateRowId ?? dialogData.candidate_row_id ?? null,
					name: d.candidateName || `Кандидат ${index + 1}`,
					lastMessage: d.lastMessage || 'Диалог начат',
					lastMessageAt: d.lastMessageAt || d.createdAt || '',
					lastCandidateMessageAt: d.lastCandidateMessageAt ?? null,
					time: formatTime(d.lastMessageAt || d.createdAt || ''),
					unread: false,
					avatar: getInitials(d.candidateName || `Кандидат ${d.candidateId || index + 1}`),
					status: d.status || 'ACTIVE',
					lifecycleStatus: dialogData.lifecycleStatus ?? null,
					manualMode: Boolean(dialogData.manualMode),
					candidateStatus: d.candidateStatus,
					rawCandidateStatus: d.rawCandidateStatus ?? null,
					skipReason: dialogData.skipReason ?? null,
					errorCode: dialogData.errorCode ?? null,
					personalDataConsent,
					...resumeFields,
					campaignId: d.campaignId,
					positionId: d.positionId ?? null,
					positionSlug: d.positionSlug ?? null,
					campaignName: d.campaignName,
					vacancy: dialogData.vacancyTitle ?? d.vacancyTitle ?? undefined,
					registrationToken: candidate?.registrationToken || null,
					messengerType: d.messengerType || null,
					screeningStatus: dialogData.screeningStatus || null,
					screeningResult: dialogData.screeningResult || null,
					screeningScore: dialogData.screeningScore || null,
					screeningLinkVisitedAt: dialogData.screeningLinkVisitedAt || null,
					screeningError: dialogData.screeningError || null,
					approvalSource: dialogData.approvalSource || null,
					isAutoApproved: Boolean(dialogData.isAutoApproved),
					relevanceScore: dialogData.relevanceScore ?? null,
					relevanceFeedback: dialogData.relevanceFeedback ?? null,
					relevanceMatchLevel: dialogData.relevanceMatchLevel ?? null,
					source: dialogData.source ?? null,
					dialogStatus: dialogData.dialogStatus ?? null
				};
			});

			setDialogs(sortDialogs(transformedDialogues));

			// Deep-link из уведомлений 647: если в URL передан ?dialogId=X — выбираем его,
			// иначе первый диалог. Логика вынесена в утилиту для юнит-тестирования.
			const initial = selectInitialDialog(transformedDialogues, getPendingRequestedDialogId(), selectedDialog);
			markRequestedDialogHandled(initial);
			if (initial !== selectedDialog) {
				setSelectedDialog(initial);
			}
		} catch (error) {
			console.error('Failed to load dialogues from all campaigns:', error);
		} finally {
			setDialogsLoading(false);
		}
	};

	const loadDialoguesByPosition = async (
		positionId: string | number,
		config?: any,
		options: { page?: number; append?: boolean; size?: number } = {},
	) => {
		const nextPage = options.page ?? 0;
		const append = Boolean(options.append);
		const requestSize = options.size ?? DIALOGUES_PAGE_SIZE;
		if (append && loadingNextDialoguesPageRef.current) return;
		if (append) {
			loadingNextDialoguesPageRef.current = true;
		}
		const requestSeq = ++dialoguesRequestSeqRef.current;
		const isCurrentRequest = () => requestSeq === dialoguesRequestSeqRef.current;
		dialogsLoadingRef.current = true;
		setDialogsLoading(true);
		try {
			const filterParams = toDialoguesParams(filtersRef.current);
			const data = await getPositionDialogues(positionId, {
				page: nextPage,
				size: requestSize,
				...(debouncedSearch ? { search: debouncedSearch } : {}),
				...filterParams,
			}, config);
			if (!isCurrentRequest()) return;

			const dialoguesList = data.content || [];

			const transformedDialogues: DialogUI[] = dialoguesList.map((d, index) => {
				const dialogData: any = d;
				const externalId = normalizeResumeId(dialogData.externalId ?? dialogData.external_id);
				const hhResumeId = normalizeResumeId(dialogData.hhResumeId ?? dialogData.hh_resume_id ?? externalId);
				const resumeId = normalizeResumeId(dialogData.resumeId ?? dialogData.resume_id ?? externalId);
				const personalDataConsent = dialogData.personalDataConsent === true;
				const resumeFields = resolveSafeDialogResumeFields({
					resumeUrl: d.resumeUrl ?? dialogData.resumeUrl ?? dialogData.resume_url ?? null,
					messengerType: d.messengerType ?? null,
					source: dialogData.source ?? null,
					hhResumeId,
					resumeId,
					externalId,
					personalDataConsent,
				});
				return {
					id: d.id,
					candidateId: d.candidateId,
					candidateRowId: dialogData.candidateRowId ?? dialogData.candidate_row_id ?? null,
					name: d.candidateName || `Кандидат ${index + 1}`,
					lastMessage: d.lastMessage || 'Диалог начат',
					lastMessageAt: d.lastMessageAt || d.createdAt || '',
					lastCandidateMessageAt: d.lastCandidateMessageAt ?? null,
					time: formatTime(d.lastMessageAt || d.createdAt || ''),
					unread: false,
					avatar: getInitials(d.candidateName || `Кандидат ${d.candidateId || index + 1}`),
					status: d.status || 'ACTIVE',
					lifecycleStatus: dialogData.lifecycleStatus ?? null,
					manualMode: Boolean(dialogData.manualMode),
					candidateStatus: d.candidateStatus,
					rawCandidateStatus: d.rawCandidateStatus ?? null,
					skipReason: dialogData.skipReason ?? null,
					errorCode: dialogData.errorCode ?? null,
					personalDataConsent,
					...resumeFields,
					campaignId: d.campaignId,
					positionId: d.positionId ?? null,
					positionSlug: d.positionSlug ?? null,
					campaignName: d.campaignName,
					// TASKNEIROKLYUCH-728: в режиме вакансии берём название из выбранной
					// вакансии — диалоги в этом списке гарантированно ей принадлежат.
					vacancy: dialogData.vacancyTitle ?? d.vacancyTitle ?? selectedVacancy?.title ?? undefined,
					registrationToken: dialogData.registrationToken ?? null,
					messengerType: d.messengerType || null,
					screeningStatus: dialogData.screeningStatus || null,
					screeningResult: dialogData.screeningResult || null,
					screeningScore: dialogData.screeningScore || null,
					screeningLinkVisitedAt: dialogData.screeningLinkVisitedAt || null,
					screeningError: dialogData.screeningError || null,
					approvalSource: dialogData.approvalSource || null,
					isAutoApproved: Boolean(dialogData.isAutoApproved),
					relevanceScore: dialogData.relevanceScore ?? null,
					relevanceFeedback: dialogData.relevanceFeedback ?? null,
					relevanceMatchLevel: dialogData.relevanceMatchLevel ?? null,
					source: dialogData.source ?? null,
					dialogStatus: dialogData.dialogStatus ?? null
				};
			});

			setDialoguesPage(data.number ?? nextPage);
			setDialoguesPageSize(data.size ?? requestSize);
			setDialoguesTotalPages(data.totalPages ?? 0);
			setDialoguesTotalElements(data.totalElements ?? transformedDialogues.length);
			setDialogs(prev => {
				if (!append) return sortDialogs(transformedDialogues);
				const dialogMap = new Map(prev.map(dialog => [dialog.id, dialog]));
				transformedDialogues.forEach(dialog => {
					const existing = dialogMap.get(dialog.id);
					const hideResumeFields = shouldHideDialogResumeFields(dialog);
					dialogMap.set(dialog.id, existing ? {
						...existing,
						...dialog,
						resumeUrl: hideResumeFields ? dialog.resumeUrl ?? null : dialog.resumeUrl ?? existing.resumeUrl,
						resumeId: hideResumeFields ? null : dialog.resumeId ?? existing.resumeId,
						hhResumeId: hideResumeFields ? null : dialog.hhResumeId ?? existing.hhResumeId,
						externalId: hideResumeFields ? null : dialog.externalId ?? existing.externalId,
						registrationToken: dialog.registrationToken ?? existing.registrationToken,
						positionSlug: dialog.positionSlug ?? existing.positionSlug,
						positionId: dialog.positionId ?? existing.positionId,
					} : dialog);
				});
				return sortDialogs(Array.from(dialogMap.values()));
			});
			const initial = selectInitialDialog(transformedDialogues, getPendingRequestedDialogId(), selectedDialog);
			if (!append && initial !== selectedDialog) {
				markRequestedDialogHandled(initial);
				setSelectedDialog(initial);
			}
		} catch (error) {
			if (isCurrentRequest()) {
				console.error('Failed to load dialogues by position:', error);
			}
		} finally {
			if (append) {
				loadingNextDialoguesPageRef.current = false;
			}
			if (isCurrentRequest()) {
				dialogsLoadingRef.current = false;
				setDialogsLoading(false);
			}
		}
	};

	const loadCampaignsData = async () => {
		setLoading(true);
		try {
			// Раньше selectedVacancyId уходил как X-Tenant-Id, потому что в ai-recruiter
			// исторически tenant_id = position_id. После TASKNEIROKLYUCH-655 фильтр
			// по вакансии делаем через positionId, а X-Tenant-Id ставит прокси
			// из JWT companyId.
			const params = selectedVacancyId ? { positionId: Number(selectedVacancyId) } : {};
			const data = await getCampaigns(params);
			const campaignsList = data.content || [];
			setCampaigns(campaignsList);

			// После загрузки кампаний, автоматически загружаем диалоги по всем кампаниям
			if (!selectedVacancyId && campaignsList.length > 0) {
				await loadDialoguesFromAllCampaigns(campaignsList);
			}
		} catch (error) {
			console.error('Failed to load campaigns:', error);
		} finally {
			setLoading(false);
		}
	};


	// Refresh dialogues without showing loading state
	const refreshDialogues = async () => {
		try {
			let allDialogues: Dialogue[] = [];

			// Load dialogues from all campaigns с текущими фильтрами (через ref,
			// чтобы поллинг не опирался на stale-замыкание).
			//
			// X-Tenant-Id не шлём: selectedVacancyId != companyId, а relevanter-back
			// под ролью SUPER_ADMIN принимает X-Tenant-Id как override и ломает
			// tenant-скоуп в ai-recruiter. Правильный tenantId (companyId из JWT) прокси
			// подставляет сам.
			const filterParams = toDialoguesParams(filtersRef.current);
			const replaceDialoguesOnRefresh = Boolean(selectedVacancyId);
			if (selectedVacancyId) {
				if (refreshingDialoguesRef.current || loadingNextDialoguesPageRef.current || dialogsLoadingRef.current) {
					return;
				}
				refreshingDialoguesRef.current = true;
				const aggregateRequestSeq = ++dialoguesRequestSeqRef.current;
				const currentPage = dialoguesPageRef.current;
				const currentPageSize = dialoguesPageSizeRef.current;
				const currentDialogsLength = dialogsLengthRef.current;
				const currentSearch = debouncedSearchRef.current;
				const refreshRequest = getDialoguesRefreshRequest({
					page: currentPage,
					pageSize: currentPageSize,
					loadedCount: currentDialogsLength,
					searchQuery: currentSearch,
				});
				const data = await getPositionDialogues(selectedVacancyId, {
					page: refreshRequest.page,
					size: refreshRequest.size,
					...(currentSearch ? { search: currentSearch } : {}),
					...filterParams,
				});
				if (aggregateRequestSeq !== dialoguesRequestSeqRef.current) return;
				allDialogues = data.content || [];
				setDialoguesPage(data.number ?? refreshRequest.page);
				setDialoguesPageSize(data.size ?? refreshRequest.size);
				setDialoguesTotalPages(data.totalPages ?? 0);
				setDialoguesTotalElements(data.totalElements ?? allDialogues.length);
			} else for (const campaign of campaigns) {
				try {
					const dialoguesList = await loadAllCampaignDialoguePages(campaign, filterParams);
					allDialogues = [...allDialogues, ...dialoguesList];
				} catch (err) {
					console.error(`Failed to refresh dialogues for campaign ${campaign.id}:`, err);
				}
			}

			// Transform dialogues to match UI format
			const transformedDialogues: DialogUI[] = allDialogues.map((d, index) => {
				const dialogData: any = d;
				const externalId = normalizeResumeId(dialogData.externalId ?? dialogData.external_id);
				const hhResumeId = normalizeResumeId(dialogData.hhResumeId ?? dialogData.hh_resume_id ?? externalId);
				const resumeId = normalizeResumeId(dialogData.resumeId ?? dialogData.resume_id ?? externalId);
				const personalDataConsent = dialogData.personalDataConsent === true;
				const resumeFields = resolveSafeDialogResumeFields({
					resumeUrl: d.resumeUrl ?? dialogData.resumeUrl ?? dialogData.resume_url ?? null,
					messengerType: dialogData.messengerType ?? d.messengerType ?? null,
					source: dialogData.source ?? null,
					hhResumeId,
					resumeId,
					externalId,
					personalDataConsent,
				});
				return {
					id: d.id,
					candidateId: d.candidateId,
					candidateRowId: dialogData.candidateRowId ?? dialogData.candidate_row_id ?? null,
					campaignId: d.campaignId,
					campaignName: d.campaignName,
					// TASKNEIROKLYUCH-728: в режиме вакансии — берём из выбранной
					// вакансии, иначе из targetPosition, проброшенного из кампании.
					vacancy: dialogData.vacancyTitle ?? d.vacancyTitle ?? (selectedVacancyId ? selectedVacancy?.title : undefined) ?? undefined,
					name: d.candidateName || `Кандидат ${d.candidateId || index + 1}`,
					lastMessage: d.lastMessage || 'Диалог начат',
					lastMessageAt: d.lastMessageAt || d.createdAt || '',
					lastCandidateMessageAt: d.lastCandidateMessageAt ?? null,
					time: formatTime(d.lastMessageAt || d.createdAt || ''),
					unread: false,
					avatar: getInitials(d.candidateName || `Кандидат ${d.candidateId || index + 1}`),
					status: d.status,
					lifecycleStatus: dialogData.lifecycleStatus ?? null,
					manualMode: Boolean(dialogData.manualMode),
					candidateStatus: d.candidateStatus,
					rawCandidateStatus: d.rawCandidateStatus ?? null,
					skipReason: dialogData.skipReason ?? null,
					errorCode: dialogData.errorCode ?? null,
					personalDataConsent,
					...resumeFields,
					positionId: (dialogData).positionId ?? null,
					positionSlug: (dialogData).positionSlug ?? null,
					registrationToken: dialogData.registrationToken ?? null,
					messengerType: dialogData.messengerType || null,
					screeningStatus: dialogData.screeningStatus || null,
					screeningResult: dialogData.screeningResult || null,
					screeningScore: dialogData.screeningScore ?? null,
					screeningLinkVisitedAt: dialogData.screeningLinkVisitedAt ?? null,
					screeningError: dialogData.screeningError ?? null,
					approvalSource: dialogData.approvalSource ?? null,
					isAutoApproved: Boolean(dialogData.isAutoApproved),
					relevanceScore: dialogData.relevanceScore ?? null,
					relevanceFeedback: dialogData.relevanceFeedback ?? null,
					relevanceMatchLevel: dialogData.relevanceMatchLevel ?? null,
					source: dialogData.source ?? null,
					dialogStatus: dialogData.dialogStatus ?? null
				};
			});

			if (replaceDialoguesOnRefresh) {
				setDialogs(sortDialogs(transformedDialogues));
			} else {
				setDialogs(prevDialogs => {
					// Обновляем существующие диалоги и добавляем новые.
					const dialogMap = new Map(prevDialogs.map(d => [d.id, d]));

					transformedDialogues.forEach(newDialog => {
						const existing = dialogMap.get(newDialog.id);
						const needsUpdate = !existing ||
							existing.lastMessage !== newDialog.lastMessage ||
							existing.lastMessageAt !== newDialog.lastMessageAt ||
							(existing.lastCandidateMessageAt ?? null) !== (newDialog.lastCandidateMessageAt ?? null) ||
							existing.candidateStatus !== newDialog.candidateStatus ||
							(existing.skipReason ?? null) !== (newDialog.skipReason ?? null) ||
							(existing.errorCode ?? null) !== (newDialog.errorCode ?? null) ||
							(existing.candidateRowId ?? null) !== (newDialog.candidateRowId ?? null) ||
							existing.status !== newDialog.status ||
							(existing.lifecycleStatus ?? null) !== (newDialog.lifecycleStatus ?? null) ||
							Boolean(existing.manualMode) !== Boolean(newDialog.manualMode) ||
							(existing.approvalSource ?? null) !== (newDialog.approvalSource ?? null) ||
							Boolean(existing.isAutoApproved) !== Boolean(newDialog.isAutoApproved) ||
							(existing.resumeUrl ?? null) !== (newDialog.resumeUrl ?? null) ||
							(existing.hhResumeId ?? null) !== (newDialog.hhResumeId ?? null) ||
							(existing.resumeId ?? null) !== (newDialog.resumeId ?? null) ||
							(existing.externalId ?? null) !== (newDialog.externalId ?? null);
						if (needsUpdate) {
							const hideResumeFields = shouldHideDialogResumeFields(newDialog);
							const merged: DialogUI = existing ? {
								...existing,
								...newDialog,
								candidateRowId: newDialog.candidateRowId ?? existing.candidateRowId,
								resumeUrl: hideResumeFields ? newDialog.resumeUrl ?? null : newDialog.resumeUrl ?? existing.resumeUrl,
								resumeId: hideResumeFields ? null : newDialog.resumeId ?? existing.resumeId,
								hhResumeId: hideResumeFields ? null : newDialog.hhResumeId ?? existing.hhResumeId,
								externalId: hideResumeFields ? null : newDialog.externalId ?? existing.externalId,
								registrationToken: existing.registrationToken ?? newDialog.registrationToken,
								positionSlug: newDialog.positionSlug ?? existing.positionSlug,
								positionId: newDialog.positionId ?? existing.positionId,
							} : newDialog;
							dialogMap.set(newDialog.id, merged);
						}
					});

					// Сортируем по времени последнего сообщения: новые выше.
					return sortDialogs(Array.from(dialogMap.values()));
				});
			}

			const currentSelectedDialog = selectedDialogRef.current;
			const currentDialogs = dialogsRef.current;

			if (replaceDialoguesOnRefresh) {
				const initial = selectInitialDialog(transformedDialogues, getPendingRequestedDialogId(), currentSelectedDialog);
				const selectedStillVisible = currentSelectedDialog
					? transformedDialogues.some(d => String(d.id) === String(currentSelectedDialog.id))
					: false;
				if (!currentSelectedDialog || !selectedStillVisible) {
					markRequestedDialogHandled(initial);
					setSelectedDialog(initial);
					return;
				}
			}

			// Update selected dialog ONLY if its data actually changed
			if (currentSelectedDialog) {
				const updatedDialog = transformedDialogues.find(d => String(d.id) === String(currentSelectedDialog.id));
				if (updatedDialog) {
					const existing = currentDialogs.find(d => String(d.id) === String(currentSelectedDialog.id)) ?? currentSelectedDialog;
					const hideResumeFields = shouldHideDialogResumeFields(updatedDialog);
					const merged: DialogUI = existing ? {
						...existing,
						...updatedDialog,
						candidateRowId: updatedDialog.candidateRowId ?? existing.candidateRowId,
						resumeUrl: hideResumeFields ? updatedDialog.resumeUrl ?? null : updatedDialog.resumeUrl ?? existing.resumeUrl,
						resumeId: hideResumeFields ? null : updatedDialog.resumeId ?? existing.resumeId,
						hhResumeId: hideResumeFields ? null : updatedDialog.hhResumeId ?? existing.hhResumeId,
						externalId: hideResumeFields ? null : updatedDialog.externalId ?? existing.externalId
					} : updatedDialog;
					// Обновляем только при реальном изменении данных.
					const hasChanges =
						currentSelectedDialog.lastMessage !== merged.lastMessage ||
						currentSelectedDialog.lastMessageAt !== merged.lastMessageAt ||
						currentSelectedDialog.candidateStatus !== merged.candidateStatus ||
						(currentSelectedDialog.skipReason ?? null) !== (merged.skipReason ?? null) ||
						(currentSelectedDialog.errorCode ?? null) !== (merged.errorCode ?? null) ||
						(currentSelectedDialog.candidateRowId ?? null) !== (merged.candidateRowId ?? null) ||
						currentSelectedDialog.status !== merged.status ||
						(currentSelectedDialog.lifecycleStatus ?? null) !== (merged.lifecycleStatus ?? null) ||
						Boolean(currentSelectedDialog.manualMode) !== Boolean(merged.manualMode) ||
						(currentSelectedDialog.approvalSource ?? null) !== (merged.approvalSource ?? null) ||
						Boolean(currentSelectedDialog.isAutoApproved) !== Boolean(merged.isAutoApproved) ||
						(currentSelectedDialog.resumeUrl ?? null) !== (merged.resumeUrl ?? null) ||
						(currentSelectedDialog.resumeId ?? null) !== (merged.resumeId ?? null) ||
						(currentSelectedDialog.hhResumeId ?? null) !== (merged.hhResumeId ?? null) ||
						(currentSelectedDialog.externalId ?? null) !== (merged.externalId ?? null);

					if (hasChanges) {
						setSelectedDialog(merged);
					}
				}
			}
		} catch (error) {
			console.error('Failed to refresh dialogues:', error);
		} finally {
			if (selectedVacancyId) {
				refreshingDialoguesRef.current = false;
			}
		}
	};

	const loadMessagesData = async (dialog: DialogUI) => {
		if (!dialog?.id || !dialog?.campaignId) return;

		const requestSeq = ++messagesLoadRequestSeqRef.current;
		const requestedDialogId = dialog.id;
		const isCurrentRequest = () =>
			requestSeq === messagesLoadRequestSeqRef.current
			&& String(selectedDialogRef.current?.id) === String(requestedDialogId);
		setMessagesLoading(true);
		try {
			const messagesList = await getDialogueMessages(dialog.campaignId, dialog.id);
			if (!isCurrentRequest()) return;

			// Transform messages to match UI format
			const transformedMessages: MessageUI[] = messagesList.map(m => {
				// API returns: senderType, content, createdAt
				const senderType = (m as any).senderType || m.sender;
				const messageText = (m as any).content || m.text;
				const messageTime = (m as any).createdAt || m.timestamp;

				const formattedTime = formatTime(messageTime);
				return {
					id: m.id,
					sender: senderType === 'AI' || senderType === 'RECRUITER' ? 'Вера' : dialog.name,
					text: messageText,
					time: formattedTime || 'время неизвестно', // Fallback if no time
					isBot: senderType === 'AI' || senderType === 'RECRUITER'
				};
			});

			setMessages(transformedMessages);
		} catch (error) {
			if (isCurrentRequest()) {
				console.error('Failed to load messages:', error);
				setMessages([]);
			}
		} finally {
			if (isCurrentRequest()) {
				setMessagesLoading(false);
			}
		}
	};

	// Refresh messages without showing loading state and only add new ones
	const refreshMessages = async () => {
		if (!selectedDialog?.id || !selectedDialog?.campaignId) return;

		const requestSeq = ++messagesRefreshRequestSeqRef.current;
		const requestedDialog = selectedDialog;
		const isCurrentRequest = () =>
			requestSeq === messagesRefreshRequestSeqRef.current
			&& String(selectedDialogRef.current?.id) === String(requestedDialog.id);
		try {
			const messagesList = await getDialogueMessages(requestedDialog.campaignId, requestedDialog.id);
			if (!isCurrentRequest()) return;

			// Transform messages to match UI format
			const transformedMessages: MessageUI[] = messagesList.map(m => {
				// API returns: senderType, content, createdAt
				const senderType = (m as any).senderType || m.sender;
				const messageText = (m as any).content || m.text;
				const messageTime = (m as any).createdAt || m.timestamp;

				const formattedTime = formatTime(messageTime);
				return {
					id: m.id,
					sender: senderType === 'AI' || senderType === 'RECRUITER' ? 'Вера' : requestedDialog.name,
					text: messageText,
					time: formattedTime || 'время неизвестно',
					isBot: senderType === 'AI' || senderType === 'RECRUITER'
				};
			});

			let shouldScroll = false;

			setMessages(prevMessages => {
				// Create a Set of existing message IDs for efficient lookup
				const existingIds = new Set(prevMessages.map(m => m.id));

				// Find new messages that aren't in the current list
				const newMessages = transformedMessages.filter(m => !existingIds.has(m.id));

				if (newMessages.length > 0) {
					// Check if there are new incoming messages (from candidate, not from bot)
					const hasNewIncomingMessages = newMessages.some(m => !m.isBot);
					if (hasNewIncomingMessages) {
						shouldScroll = true;
					}

					// Combine existing messages with new ones
					const combined = [...prevMessages, ...newMessages];
					return combined;
				}

				// If no new messages, return existing array to prevent re-render
				return prevMessages;
			});

			// Only scroll if there are new incoming messages
			if (shouldScroll) {
				setTimeout(() => scrollToBottom(), 100);
			}
		} catch (error) {
			if (isCurrentRequest()) {
				console.error('Failed to refresh messages:', error);
			}
		}
	};

	const formatTime = (timestamp: string, onlyTime = false): string => {
		if (!timestamp) return '';

		// Handle different date formats (ISO string, timestamp, etc.)
		let date: Date;
		if (typeof timestamp === 'string') {
			// Handle Java LocalDateTime format (2025-09-28T13:45:23.123456)
			if (timestamp.includes('T')) {
				// Parse ISO-like format directly
				date = new Date(timestamp);
			} else {
				date = new Date(timestamp);
			}
		} else {
			date = new Date(timestamp);
		}

		// Check if date is valid
		if (isNaN(date.getTime())) {
			console.warn('Invalid date:', timestamp);
			return '';
		}

		const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

		// If only time is requested, return just the time
		if (onlyTime) {
			return time;
		}

		const now = new Date();
		const isToday = date.toDateString() === now.toDateString();

		if (isToday) {
			return `сегодня в ${time}`;
		} else {
			const dateStr = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
			return `${dateStr} в ${time}`;
		}
	};

	const getInitials = (name: string): string => {
		if (!name) return '?';
		// Поддержка: пробелы (любое кол-во), имя+фамилия через дефис ("Михаил-Лебедев"),
		// двойные пробелы. Гарантирует "МЛ" из "Михаил Лебедев", "АА" из "Анна Антонова" и т.п.
		const parts = name.trim().split(/[\s-]+/).filter(p => p.length > 0);
		if (parts.length >= 2) {
			return ((parts[0][0] ?? '') + (parts[1][0] ?? '')).toUpperCase();
		}
		if (parts.length === 1) {
			// Одно слово — отображаем только первую букву (не "АН" из "Аноним").
			return parts[0][0].toUpperCase();
		}
		return '?';
	};

	/**
	 * Статус кандидата приходит с backend как бизнес-проекция REL-17.
	 * Mailing-only raw-статусы остаются в rawCandidateStatus и здесь не показываются.
	 */
	type EffectiveCandidateStatus = CandidateStatusFilter;

	const getEffectiveCandidateStatus = (
		status?: CandidateStatusFilter | null,
	): EffectiveCandidateStatus | null => {
		return status ?? null;
	};

	const getCandidateStatusText = (status: EffectiveCandidateStatus): string => {
		return CANDIDATE_STATUS_LABEL[status];
	};

	const getCandidateStatusColor = (status: EffectiveCandidateStatus): string => {
		return CANDIDATE_STATUS_BADGE_CLASS[status];
	};

	// Helper: hex color to rgba for subtle selection backgrounds
	const hexToRgba = (hex: string, alpha: number): string => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	};

	const handleEmojiClick = (emojiData: EmojiClickData) => {
		setMessage(prevMessage => prevMessage + emojiData.emoji);
		setShowEmojiPicker(false);
	};

	const handleSendMessage = async () => {
		if (message.trim() && selectedDialog?.id && selectedDialog?.campaignId) {
			try {
				// Store message text before clearing
				const messageText = message;
				setMessage(''); // Clear input immediately for better UX

				// Send message to backend
				const newMessage = await sendDialogueMessage(selectedDialog.campaignId, selectedDialog.id, messageText);

				// Add the new message to the messages list immediately with proper formatting
				const formattedTime = formatTime(newMessage.timestamp || new Date().toISOString());
				const newFormattedMessage: MessageUI = {
					id: newMessage.id || `temp-${Date.now()}`,
					sender: 'Вера',
					text: newMessage.text || messageText,
					time: formattedTime || formatTime(new Date().toISOString()),
					isBot: true // Recruiter messages show as "bot" (right side)
				};

				setMessages(prevMessages => {
					// Check if this message already exists (by id or content)
					const exists = prevMessages.some(m =>
						m.id === newFormattedMessage.id ||
						(m.text === newFormattedMessage.text && m.time === newFormattedMessage.time)
					);

					if (!exists) {
						return [...prevMessages, newFormattedMessage];
					}
					return prevMessages;
				});

				// Scroll to bottom immediately after adding new message
				setTimeout(() => scrollToBottom(), 50);
			} catch (error) {
				console.error('Failed to send message:', error);
				toast.error(formatSendMessageError(error));
				// Restore message if sending failed
				setMessage(message);
			}
		}
	};

	const handleDialogSelect = (dialog: DialogUI) => {
		if (String(selectedDialogRef.current?.id) === String(dialog.id)) {
			return;
		}
		// Clear old messages to avoid scroll firing on stale data
		setMessages([]);
		// If we have an active search, scroll to the matching message after loading
		setShouldScrollToMatch(!!searchQuery.trim());
		setSelectedDialog(dialog);
	};

	const handleToggleDialogue = async (nextStopped: boolean) => {
		if (selectedDialog && selectedDialog.campaignId) {
			try {
				const action = nextStopped ? 'pause' : 'resume';
				const updatedDialogue = await toggleDialogue(selectedDialog.campaignId, selectedDialog.id, action);
				const updatedData = updatedDialogue as any;

				const newStatus = updatedData?.status || (nextStopped ? 'PAUSED' : 'QUALIFYING');
				const newLifecycleStatus = updatedData?.lifecycleStatus || (nextStopped ? 'STOPPED' : 'ACTIVE');
				const newManualMode = typeof updatedData?.manualMode === 'boolean'
					? updatedData.manualMode
					: nextStopped;
				setSelectedDialog(prev => prev ? ({
					...prev,
					status: newStatus,
					lifecycleStatus: newLifecycleStatus,
					manualMode: newManualMode
				}) : null);

				setDialogs(prev => prev.map(dialog =>
					dialog.id === selectedDialog.id
						? {
							...dialog,
							status: newStatus,
							lifecycleStatus: newLifecycleStatus,
							manualMode: newManualMode
						}
						: dialog
				));

				setIsAiStopped(isDialogueAiStopped({
					status: newStatus,
					lifecycleStatus: newLifecycleStatus,
					manualMode: newManualMode
				}));
			} catch (error) {
				console.error('Failed to toggle dialogue:', error);
				toast.error('Не удалось изменить режим ИИ диалога');
			}
		}
	};

	const handleCandidateStatus = async (status: ManualCandidateDecisionStatus) => {
		const campaignId = normalizeResumeId(selectedDialog?.campaignId);
		const candidateStatusActionId = resolveDialogCandidateStatusActionId(selectedDialog);
		if (!selectedDialog || !campaignId || !candidateStatusActionId) {
			toast.error('Не удалось обновить статус кандидата: нет идентификатора кандидата');
			return;
		}

		try {
			await updateCandidateStatus(
				campaignId,
				candidateStatusActionId,
				status,
				{ sendDecisionNotification: true }
			);
			const projectedStatus = resolveManualDecisionCandidateStatus(status);

			setSelectedDialog(prev => prev ? ({
				...prev,
				candidateStatus: projectedStatus,
				rawCandidateStatus: status
			}) : null);

			setDialogs(prevDialogs =>
				prevDialogs.map(d =>
					d.id === selectedDialog.id
						? { ...d, candidateStatus: projectedStatus, rawCandidateStatus: status }
						: d
				)
			);

		} catch (error) {
			console.error('Failed to update candidate status:', error);
			toast.error('Не удалось обновить статус кандидата');
		}
	};

	// Function to convert URLs in text to clickable links
	const linkifyText = (text: string) => {
		if (!text) return null;

		// Regular expression to match URLs (http, https, www)
		const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
		const parts = text.split(urlRegex);

		return parts.map((part, index) => {
			if (part.match(urlRegex)) {
				// Add https:// if URL starts with www
				const href = part.startsWith('www.') ? `https://${part}` : part;
				return (
					<a
						key={index}
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:opacity-80 transition-opacity"
						onClick={(e) => e.stopPropagation()}
					>
						{part}
					</a>
				);
			}
			return part;
		});
	};

	const rightPanelWidth = isAdaptivePanels ? 268 : 320;
	const panelPaddingClass = isVeryShortViewport ? 'p-3' : isAdaptivePanels ? 'p-4' : 'p-6';
	const sectionSpacingClass = isVeryShortViewport ? 'space-y-2' : isShortViewport ? 'space-y-3' : 'space-y-4';
	const actionButtonClass = isVeryShortViewport ? 'px-3 py-1.5 text-sm' : isShortViewport ? 'px-3 py-2 text-sm' : 'px-4 py-2';
	const actionBarClass = isVeryShortViewport
		? 'flex-shrink-0 -mx-3 px-3 pt-2 pb-1 space-y-1.5 bg-white border-t border-gray-100'
		: isShortViewport
			? 'flex-shrink-0 -mx-4 px-4 pt-3 pb-1 space-y-2 bg-white border-t border-gray-100'
			: 'flex-shrink-0 -mx-6 px-6 pt-4 pb-1 space-y-2 bg-white border-t border-gray-100';
	const infoValueTextClass = isVeryShortViewport ? 'text-sm' : '';
	const linkTextClass = isVeryShortViewport ? 'text-xs' : 'text-sm';
	const selectedDialogResumeLink = resolveDialogResumeLink(selectedDialog);
	const selectedDialogResumeLinkLabel = getDialogResumeLinkLabel(selectedDialog);
	const selectedDialogEStaffResumeData = resolveDialogEStaffResumeData(selectedDialog);
	const selectedDialogHasResumeAction = Boolean(selectedDialogResumeLink || selectedDialogEStaffResumeData);

	const openEStaffResume = (dialog: DialogResumeLinkData | null | undefined) => {
		const resumeData = resolveDialogEStaffResumeData(dialog);
		if (!resumeData) return;
		setSelectedEStaffResume(resumeData);
		setEstaffResumeModalOpen(true);
	};
	const dialoguesPagination = getDialoguesPaginationView({
		selectedVacancyId,
		loading: dialogsLoading,
		page: dialoguesPage,
		pageSize: dialoguesPageSize,
		totalPages: dialoguesTotalPages,
		totalElements: dialoguesTotalElements,
		loadedCount: dialogs.length,
		searchQuery,
	});
	const canLoadMoreDialogues = dialoguesPagination.canLoadMore;
	const showDialoguesPagination = dialoguesPagination.showPagination;

	const loadNextDialoguesPage = () => {
		if (!selectedVacancyId || !canLoadMoreDialogues) return;
		loadDialoguesByPosition(selectedVacancyId, undefined, {
			page: dialoguesPage + 1,
			append: true,
		});
	};

	const handleDialoguesScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (distanceToBottom < 160) {
			loadNextDialoguesPage();
		}
	};

	const goToDialoguesPage = (page: number) => {
		if (!selectedVacancyId) return;
		loadDialoguesByPosition(selectedVacancyId, undefined, {
			page: Math.max(0, Math.min(page, dialoguesPagination.totalPages - 1)),
			size: dialoguesPagination.pageSize,
			append: false,
		});
	};

	// Раньше здесь был ранний return с full-screen плейсхолдером «Диалоги отсутствуют»,
	// но он полностью убирал панель фильтров — пользователь не мог снять фильтр,
	// когда выбранная комбинация давала 0 результатов. Теперь пустое состояние
	// рендерится внутри основного layout (см. блок в центральной части), а
	// панель фильтров остаётся видимой всегда.

	return (
		<div className="h-full min-h-0 min-w-0 flex flex-col bg-white">
			{/* Панель фильтров над списком диалогов и рабочей областью */}
			<div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 relative z-20">
				<DialogFiltersBar value={filters} onChange={setFilters} />
			</div>

			<div className="flex-1 min-h-0 flex">
			{/* Dialogs List */}
			<div className={`${isCompactDialogsList ? 'w-[72px]' : isAdaptivePanels ? 'w-72' : 'w-80'} min-h-0 flex-shrink-0 border-r border-gray-200 flex flex-col`}>
				{/* Search */}
				{!isCompactDialogsList && <div className="px-3 py-3 border-b border-gray-200">
					<div className="relative">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="Поиск по ФИО или сообщениям..."
							className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
							onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}`; }}
							onBlur={(e) => { e.currentTarget.style.boxShadow = ''; }}
						/>
						{searchQuery && (
							<button
								onClick={() => setSearchQuery('')}
								className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
							>
								<XMarkIcon className="w-4 h-4" />
							</button>
						)}
					</div>
				</div>}

				{/* Filter */}
				<div className="hidden px-3 py-5 border-b border-gray-200">
					<DropdownMenu.Root>
						<DropdownMenu.Trigger asChild>
							<button
								className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none text-sm"
								onFocus={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${colors.primary}`; }}
								onBlur={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = ''; }}
							>
								<BriefcaseIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
								<span className="text-gray-700 truncate flex-1 text-left">
									{selectedCampaign
										? campaigns.find((c) => String(c.id) === selectedCampaign)?.name
										: 'Все вакансии'}
								</span>
								<ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
							</button>
						</DropdownMenu.Trigger>

						<DropdownMenu.Portal>
							<DropdownMenu.Content
								className="w-[304px] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50 max-h-[400px] overflow-y-scroll show-scrollbar"
								align="start"
								sideOffset={5}
							>
								{campaigns.length === 0 ? (
									<div className="px-4 py-2 text-sm text-gray-500">Нет активных кампаний</div>
								) : (
									<>
										<DropdownMenu.Item
											className="px-4 py-3 hover:bg-gray-50 cursor-pointer outline-none border-b border-gray-100"
											onSelect={() => setSelectedCampaign(null)}
										>
											<div className="text-sm font-medium text-gray-900">
												Все вакансии
											</div>
										</DropdownMenu.Item>
										{campaigns.map(campaign => (
											<DropdownMenu.Item
												key={campaign.id}
												className="px-4 py-3 hover:bg-gray-50 cursor-pointer outline-none border-b border-gray-100 last:border-b-0"
												onSelect={() => setSelectedCampaign(String(campaign.id))}
											>
												<div
													className={`text-sm font-medium ${
														String(selectedCampaign) === String(campaign.id) ? 'text-gray-900' : 'text-gray-900'
													}`}
													style={String(selectedCampaign) === String(campaign.id) ? { color: colors.primary } : undefined}
												>
													{campaign.name}
												</div>
											</DropdownMenu.Item>
										))}
									</>
								)}
							</DropdownMenu.Content>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
				</div>

				{/* Dialog list */}
				<div className="flex-1 overflow-y-auto relative" onScroll={handleDialoguesScroll}>
					{searchLoading && (
						<div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-200 overflow-hidden z-10">
							<div className="h-full w-1/3 animate-pulse rounded" style={{ backgroundColor: colors.primary }} />
						</div>
					)}
					{dialogsLoading && !searchLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="text-gray-500">Загрузка...</div>
						</div>
					) : filteredDialogs.length === 0 && searchQuery ? (
						<div className="flex items-center justify-center py-8">
							<div className="text-gray-500 text-sm">
								{searchLoading ? 'Поиск по сообщениям...' : 'Ничего не найдено'}
							</div>
						</div>
					) : filteredDialogs.length === 0 ? (
						<div className="flex items-center justify-center py-8 px-4">
							<div className="text-gray-500 text-sm text-center">
								Диалоги отсутствуют
							</div>
						</div>
					) : (
						(() => {
							// В левом списке показываем статус кандидата теми же значениями, что и фильтр "Статус кандидата".

							return filteredDialogs.map((dialog) => {
								const isSelected = selectedDialog?.id === dialog.id;
								const listBadge = resolveDialogBadge({ dialogStatus: dialog.dialogStatus });
								const candidateBadgeStatus = getEffectiveCandidateStatus(dialog.candidateStatus);
								const hasListStatusRow = Boolean(candidateBadgeStatus || listBadge);
								const dialogResumeLink = resolveDialogResumeLink(dialog);
								const dialogEStaffResumeData = resolveDialogEStaffResumeData(dialog);
								return (
									<div
										key={dialog.id}
										data-testid="dialog-list-item"
										data-dialog-id={dialog.id}
										onClick={() => handleDialogSelect(dialog)}
										className={`cursor-pointer border-b border-gray-100 transition-colors ${
											isCompactDialogsList
												? 'flex items-center justify-center px-0 py-3'
												: 'grid grid-cols-[2.5rem,minmax(0,1fr)] gap-x-3 gap-y-1 px-4 py-3 items-start'
										} ${
											isSelected
												? 'border-l-4'
												: 'hover:bg-gray-50 border-l-4 border-l-transparent'
										}`}
										style={isSelected ? {
											backgroundColor: hexToRgba(colors.primary, 0.08),
											borderLeftColor: colors.primary
										} : undefined}
									>
										<div className="relative w-10 h-10 flex-shrink-0">
											<div
												className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
												style={{ background: `linear-gradient(to bottom right, ${colors.ai}, ${colors.primary})` }}
											>
												{dialog.avatar}
											</div>
											{(dialog.approvalSource === 'AI_AUTO' || dialog.isAutoApproved) && (
												<AiApprovedBadgeIcon className="absolute -top-1 -right-1 w-6 h-6" />
											)}
										</div>
										{!isCompactDialogsList && (
											<div className="relative min-w-0">
												<div className="flex items-center justify-between gap-2 mb-1">
													<div className="min-w-0">
														<h3
															className={`min-w-0 truncate ${
																dialog.dialogStatus === 'PROBLEM'
																	? 'font-bold text-red-600'
																	: 'font-medium text-gray-900'
															}`}
														>
															{dialogEStaffResumeData ? (
																<button
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		openEStaffResume(dialog);
																	}}
																	className="max-w-full truncate hover:underline bg-transparent border-0 p-0 text-left cursor-pointer"
																	style={{ font: 'inherit', color: 'inherit' }}
																>
																	{dialog.name}
																</button>
															) : dialogResumeLink ? (
																<a
																	href={dialogResumeLink}
																	target="_blank"
																	rel="noopener noreferrer"
																	onClick={(e) => e.stopPropagation()}
																	className="hover:underline"
																>
																	{dialog.name}
																</a>
															) : (
																dialog.name
															)}
														</h3>
													</div>
													<span className="text-xs text-gray-500 flex-shrink-0">{dialog.time}</span>
												</div>
												<div className="h-5 min-w-0">
													<p className={`text-sm truncate ${dialog._searchPreview && searchQuery ? 'text-yellow-700' : 'text-gray-600'}`}>
														{dialog._searchPreview && searchQuery
															? dialog._searchPreview
															: dialog.lastMessage}
													</p>
												</div>
											</div>
										)}
										{!isCompactDialogsList && hasListStatusRow && (
											<div
												className="col-span-2 flex items-center gap-2 min-w-0 pt-0.5"
												data-testid="dialog-list-status-row"
											>
												{candidateBadgeStatus && (
													<span
														className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium leading-4 shrink-0 max-w-[9.25rem] truncate ${getCandidateStatusColor(candidateBadgeStatus)}`}
														title="Статус кандидата"
														data-testid="dialog-list-candidate-status"
													>
														{getCandidateStatusText(candidateBadgeStatus)}
													</span>
												)}
												<span className="flex-1 min-w-0" aria-hidden="true" />
												{listBadge && (
													<span
														className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium leading-4 shrink-0 max-w-[8rem] truncate ${listBadge.className}`}
														title="Статус диалога"
														data-testid="dialog-list-dialog-status"
													>
														{listBadge.label}
													</span>
												)}
											</div>
										)}
									</div>
								);
							});
				})()
				)}
				{canLoadMoreDialogues && (
					<div className="py-3 flex justify-center">
						<button
							type="button"
							onClick={loadNextDialoguesPage}
							className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
						>
							Загрузить еще
						</button>
					</div>
				)}
				{showDialoguesPagination && (
					<div className="sticky bottom-0 bg-white border-t border-gray-200 px-3 py-2 flex items-center justify-between gap-2 text-xs text-gray-600">
						<button
							type="button"
							onClick={() => goToDialoguesPage(dialoguesPagination.currentPage - 1)}
							disabled={dialoguesPagination.currentPage <= 0}
							className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
						>
							Назад
						</button>
						<span className="truncate">
							{dialoguesPagination.currentPage + 1} / {dialoguesPagination.totalPages} · {dialoguesTotalElements}
						</span>
						<button
							type="button"
							onClick={() => goToDialoguesPage(dialoguesPagination.currentPage + 1)}
							disabled={dialoguesPagination.currentPage + 1 >= dialoguesPagination.totalPages}
							className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
						>
							Вперед
						</button>
					</div>
				)}
				</div>
			</div>

			{/* Chat Area */}
			<div className="flex-1 min-w-0 flex flex-col min-h-0">
				{/* Chat Header */}
				<div className={`border-b border-gray-200 ${isAdaptivePanels ? 'px-4 py-3' : 'px-6 py-4'}`}>
					<div className={`flex justify-between gap-3 ${isAdaptivePanels ? 'items-start flex-wrap' : 'items-center'}`}>
						<div className="flex items-center gap-3 min-w-0 flex-1">
							<div
								className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
								style={{ background: `linear-gradient(to bottom right, ${colors.ai}, ${colors.primary})` }}
							>
								{selectedDialog?.avatar || '?'}
							</div>
							<div className="min-w-0">
							<h2
								className={`font-semibold text-gray-900 truncate ${selectedDialogHasResumeAction ? 'cursor-pointer hover:underline' : ''} ${isAdaptivePanels ? 'text-base' : ''}`}
								style={selectedDialogHasResumeAction && isNameHover ? { color: colors.primary } : undefined}
								onMouseEnter={() => setIsNameHover(true)}
								onMouseLeave={() => setIsNameHover(false)}
									onClick={(e) => {
										e.stopPropagation();
										if (selectedDialogEStaffResumeData) {
											openEStaffResume(selectedDialog);
										} else if (selectedDialogResumeLink) {
											window.open(selectedDialogResumeLink, '_blank', 'noopener,noreferrer');
										}
									}}
								>
									{selectedDialog?.name || 'Не выбран'}
								</h2>
								<p className="text-sm text-gray-600 break-words">{selectedDialog?.vacancy || ''}</p>
							</div>
						</div>
						<div className={`flex items-center gap-2 ${isAdaptivePanels ? 'ml-auto' : ''}`}>
							<span className={`text-gray-600 ${isAdaptivePanels ? 'text-xs' : 'text-sm'}`}>Остановить ИИ диалог</span>
							<Switch.Root
								checked={isAiStopped}
								onCheckedChange={handleToggleDialogue}
								disabled={!selectedDialog?.campaignId}
								className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
								style={{ backgroundColor: isAiStopped ? colors.primary : '#e5e7eb' }}
							>
								<Switch.Thumb
									className={`${
										isAiStopped ? 'translate-x-6' : 'translate-x-1'
									} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
								/>
							</Switch.Root>
						</div>
					</div>
				</div>

				{/* Messages */}
				<div ref={messagesContainerRef} className={`flex-1 min-h-0 overflow-y-auto ${isAdaptivePanels ? 'px-4 py-3 space-y-3' : 'px-6 py-4 space-y-4'}`}>
					{messagesLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="text-gray-500">Загрузка сообщений...</div>
						</div>
					) : messages.length === 0 ? (
						<div className="flex items-center justify-center py-8">
							<div className="text-gray-500">Нет сообщений</div>
						</div>
					) : (
						messages.map((msg) => (
						<div
							key={msg.id}
							data-message-id={msg.id}
							className={`flex ${msg.isBot ? 'justify-end' : 'justify-start'}`}
						>
							<div className={`max-w-2xl ${msg.isBot ? 'order-1' : 'order-2'}`}>
								<div className="flex items-end gap-2">
									{!msg.isBot && (
										<div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
											{getInitials(msg.sender)}
										</div>
									)}
									<div
									className={`px-4 py-2 rounded-2xl ${
										msg.isBot
											? 'text-white'
											: 'bg-gray-100 text-gray-900'
									}`}
									style={msg.isBot ? { backgroundColor: colors.primary } : undefined}
									>
										<p className="text-sm whitespace-pre-wrap">{linkifyText(msg.text)}</p>
									</div>
								</div>
								<p className={`text-xs text-gray-500 mt-1 ${!msg.isBot ? 'ml-10' : 'text-right'}`}>
									{msg.time}
								</p>
							</div>
						</div>
						))
					)}
					{/* Scroll anchor element */}
					<div ref={messagesEndRef} />
				</div>

				{/* Message Input */}
				<div className={`border-t border-gray-200 ${isAdaptivePanels ? 'px-4 py-3' : 'px-6 py-4'}`}>
					<div className="flex items-end gap-2 relative">
						<textarea
							ref={messageInputRef}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey) {
									e.preventDefault();
									handleSendMessage();
								}
							}}
							placeholder="Напишите сообщение"
							rows={1}
							className="flex-1 px-4 py-2 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none leading-5"
						/>
						<div className="relative" ref={emojiPickerRef}>
							<button
								onClick={() => setShowEmojiPicker(!showEmojiPicker)}
								className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
							>
								<FaceSmileIcon className="w-5 h-5" />
							</button>
							{showEmojiPicker && (
								<div className="absolute bottom-12 right-0 z-50">
									<EmojiPicker
										onEmojiClick={handleEmojiClick}
										autoFocusSearch={false}
										theme={Theme.LIGHT}
										searchDisabled={false}
										height={400}
										width={320}
										lazyLoadEmojis={true}
										previewConfig={{
											showPreview: false
										}}
									/>
								</div>
							)}
						</div>
							<button
								onClick={handleSendMessage}
								className="p-2 text-white rounded-full transition-colors"
								style={{ backgroundColor: colors.primary }}
								onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover)}
								onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary)}
							>
							<PaperAirplaneIcon className="w-5 h-5" />
						</button>
					</div>
				</div>
			</div>

			{/* Right Panel - Candidate Info */}
			<div
				className={`${panelPaddingClass} min-h-0 flex flex-col flex-shrink-0 overflow-hidden border-l border-gray-200`}
				style={{
					width: `${rightPanelWidth}px`,
					minWidth: `${rightPanelWidth}px`
				}}
			>
				<div className="min-h-0 flex-1 overflow-y-auto">
					<h3 className={`font-semibold text-gray-900 ${isVeryShortViewport ? 'mb-2 text-base' : isShortViewport ? 'mb-3 text-lg' : 'mb-4'}`}>Информация о кандидате</h3>
					<RelevanceBlock
						score={selectedDialog?.relevanceScore ?? null}
						matchLevel={selectedDialog?.relevanceMatchLevel ?? null}
						feedback={selectedDialog?.relevanceFeedback ?? null}
						className={isVeryShortViewport ? 'mb-2' : isShortViewport ? 'mb-3' : 'mb-4'}
					/>
					<div className={sectionSpacingClass}>
						<div>
							<p className="text-sm text-gray-500">Имя</p>
							<p className={`font-medium break-words ${infoValueTextClass}`}>{selectedDialog?.name || '-'}</p>
						</div>
						<div>
							<p className="text-sm text-gray-500">Вакансия</p>
							{/* TASKNEIROKLYUCH-728: показываем название вакансии (Campaign.targetPosition),
								а не название рассылки. Если targetPosition не пришёл — fallback на название
								рассылки, чтобы не было пустого поля. */}
							<p className={`font-medium break-words ${infoValueTextClass}`}>{selectedDialog?.vacancy || selectedDialog?.campaignName || '-'}</p>
						</div>
						<div>
							<p className="text-sm text-gray-500">Мессенджер</p>
							{selectedDialog?.messengerType ? (
								<span
									className="inline-flex items-center justify-center text-xs font-medium"
									style={{
										padding: '4px 8px',
										borderRadius: '6px',
										...(selectedDialog.messengerType === 'TELEGRAM' && {
											backgroundColor: '#EBF1FF',
											color: '#375DFB'
										}),
										...(selectedDialog.messengerType === 'WHATSAPP' && {
											backgroundColor: '#EFFAF6',
											color: '#38C793'
										}),
										...(selectedDialog.messengerType === 'MAX' && {
											backgroundColor: '#EEEBFF',
											color: '#6E3FF3'
										}),
										...(selectedDialog.messengerType === 'HH' && {
											backgroundColor: '#FFF4ED',
											color: '#E16349'
										}),
										...(selectedDialog.messengerType === 'AVITO' && {
											backgroundColor: '#ECFDF3',
											color: '#039855'
										}),
										...(!['TELEGRAM', 'WHATSAPP', 'MAX', 'HH', 'AVITO'].includes(selectedDialog.messengerType) && {
											backgroundColor: '#F3F4F6',
											color: '#6B7280'
										})
									}}
								>
									{selectedDialog.messengerType === 'TELEGRAM' ? 'Telegram' :
									 selectedDialog.messengerType === 'WHATSAPP' ? 'WhatsApp' :
									 selectedDialog.messengerType === 'MAX' ? 'MAX' :
									 selectedDialog.messengerType === 'HH' ? 'HeadHunter' :
									 selectedDialog.messengerType === 'AVITO' ? 'Авито' :
									 selectedDialog.messengerType}
								</span>
							) : (
								<p className="font-medium">-</p>
							)}
						</div>
						<div>
							<p className="text-sm text-gray-500">Статус диалога</p>
							{(() => {
								const badge = selectedDialog ? resolveDialogBadge(selectedDialog) : null;
								const lifecycleLabel = isDialogueLifecycleStopped(selectedDialog) ? 'Остановлен' : 'Активен';
								return (
									<div className="mt-1 flex flex-wrap items-center gap-2">
										{badge ? (
											<span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${badge.className}`}>
												{badge.label}
											</span>
										) : null}
										<span className={`font-medium text-gray-700 ${infoValueTextClass}`}>{lifecycleLabel}</span>
									</div>
								);
							})()}
						</div>
						{/* TASKNEIROKLYUCH-754: «Источник» — отклик / поиск hh / база. Бэк
							резолвит из CampaignCandidate (snapshot at campaign creation), так что
							значение не сбивается после первого исходящего invitation в cold-search. */}
						<div>
							<p className="text-sm text-gray-500">Источник</p>
							{selectedDialog?.source ? (
								<span
									className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-md ${SOURCE_CLASS[selectedDialog.source as CandidateSourceFilter] ?? 'bg-gray-100 text-gray-600 border border-gray-200'}`}
								>
									{SOURCE_LABEL[selectedDialog.source as CandidateSourceFilter] ?? selectedDialog.source}
								</span>
							) : (
								<p className="font-medium">-</p>
							)}
						</div>
						<div>
							<p className="text-sm text-gray-500 mb-2">Статус кандидата</p>
							{(() => {
								const effective = getEffectiveCandidateStatus(
									selectedDialog?.candidateStatus
								);
								if (!effective) return <p className="font-medium">-</p>;
								return (
									<span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${getCandidateStatusColor(effective)}`}>
										{getCandidateStatusText(effective)}
									</span>
								);
							})()}
							{selectedDialog?.approvalSource === 'AI_AUTO' && (
								<div className="mt-2">
									<div className={`inline-flex items-center rounded-full text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 ${isVeryShortViewport ? 'gap-1.5 px-2 py-1' : 'gap-2 px-3 py-1'}`}>
										<AiApprovedBadgeIcon className={`${isVeryShortViewport ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
										<span>Одобрено нейросетью</span>
									</div>
								</div>
							)}
						</div>
						{(selectedDialog?.registrationToken || selectedDialog?.screeningStatus != null || selectedDialog?.screeningLinkVisitedAt != null || selectedDialog?.screeningError) && (
							<div>
								<p className="text-sm text-gray-500">AI-скрининг</p>
								<p className="font-medium">
									{selectedDialog.screeningResult === 'PASSED'
										? (selectedDialog.screeningScore != null ? `Прошёл (${selectedDialog.screeningScore}/10)` : 'Прошёл')
										: selectedDialog.screeningResult === 'FAILED'
										? (selectedDialog.screeningScore != null ? `Не прошёл (${selectedDialog.screeningScore}/10)` : 'Не прошёл')
										: selectedDialog.screeningResult === 'PARTIALLY'
										? (selectedDialog.screeningScore != null ? `Частично прошёл (${selectedDialog.screeningScore}/10)` : 'Частично прошёл')
										: selectedDialog.screeningError
										? (selectedDialog.screeningError === 'QUESTIONS_NOT_VOICED' ? 'Вопросы не озвучены' : selectedDialog.screeningError)
										: selectedDialog.screeningStatus === 'IN_PROGRESS'
										? 'В процессе'
										: selectedDialog.screeningLinkVisitedAt
										? 'Заходил по ссылке'
										: 'Не заходил по ссылке'}
								</p>
							</div>
						)}
						<div>
							<p className="text-sm text-gray-500 mb-2">Ссылки</p>
							<div className="space-y-2">
								{selectedDialogEStaffResumeData ? (
									<button
										type="button"
										onClick={() => openEStaffResume(selectedDialog)}
										className={`flex items-center gap-2 font-medium hover:opacity-80 transition-opacity text-[#375DFB] bg-transparent border-0 p-0 cursor-pointer text-left w-full ${linkTextClass}`}
									>
										<ResumeIcon className={`${isShortViewport ? 'w-6 h-6' : 'w-7 h-7'} flex-shrink-0`} />
										<span className="min-w-0 break-words">Резюме E-Staff</span>
									</button>
								) : selectedDialogResumeLink && (
									<a
										href={selectedDialogResumeLink}
										target="_blank"
										rel="noopener noreferrer"
										className={`flex items-center gap-2 font-medium hover:opacity-80 transition-opacity text-[#375DFB] ${linkTextClass}`}
									>
										<ResumeIcon className={`${isShortViewport ? 'w-6 h-6' : 'w-7 h-7'} flex-shrink-0`} />
										<span className="min-w-0 break-words">{selectedDialogResumeLinkLabel}</span>
										<ExternalLinkIcon className="w-5 h-5 flex-shrink-0" />
									</a>
								)}
								{(selectedDialog?.positionSlug || (selectedVacancy as any)?.publicSlug || selectedVacancyId) && selectedDialog?.registrationToken && (
									<button
										type="button"
										onClick={() => {
											const slug = selectedDialog?.positionSlug || (selectedVacancy as any)?.publicSlug || selectedVacancyId;
											const userId = useAuthStore.getState().user?.id;
											const recr = userId ? btoa(String(userId)) : null;
											const recrParam = recr ? `&recr=${recr}` : '';
											const url = `${window.location.origin}/vacancy/${slug}?token=${selectedDialog.registrationToken}${recrParam}`;
											navigator.clipboard.writeText(url).then(() => {
												setCopiedLinkKey('interview');
												toast.success('Скопировано');
												setTimeout(() => setCopiedLinkKey(null), 2000);
											}).catch(() => toast.error('Не удалось скопировать'));
										}}
										className={`flex items-center gap-2 font-medium hover:opacity-80 transition-opacity text-[#375DFB] bg-transparent border-0 p-0 cursor-pointer text-left w-full ${linkTextClass}`}
									>
										<InterviewIcon className={`${isShortViewport ? 'w-6 h-6' : 'w-7 h-7'} flex-shrink-0`} />
										<span className="min-w-0 break-words">{copiedLinkKey === 'interview' ? 'Скопировано' : 'Запись на интервью'}</span>
										<ExternalLinkIcon className="w-5 h-5 flex-shrink-0" />
									</button>
								)}
								{selectedVacancyId && hasAiScreeningQuestions && (
									<button
										type="button"
										onClick={() => {
											const base = (process.env.REACT_APP_CANDIDATE_BASE_URL || window.location.origin).toString().replace(/\/+$/, '');
											const url = selectedDialog?.registrationToken
												? `${base}/candidate/interview/${selectedVacancyId}?token=${selectedDialog.registrationToken}`
												: `${base}/candidate/interview/${selectedVacancyId}`;
											navigator.clipboard.writeText(url).then(() => {
												setCopiedLinkKey('ai-screening');
												toast.success('Скопировано');
												setTimeout(() => setCopiedLinkKey(null), 2000);
											}).catch(() => toast.error('Не удалось скопировать'));
										}}
										className={`flex items-center gap-2 font-medium hover:opacity-80 transition-opacity text-[#375DFB] bg-transparent border-0 p-0 cursor-pointer text-left w-full ${linkTextClass}`}
									>
										<AiScreeningIcon className={`${isShortViewport ? 'w-6 h-6' : 'w-7 h-7'} flex-shrink-0`} />
										<span className="min-w-0 break-words">{copiedLinkKey === 'ai-screening' ? 'Скопировано' : 'Ссылка на AI-скрининг'}</span>
										<ExternalLinkIcon className="w-5 h-5 flex-shrink-0" />
									</button>
								)}
								{!selectedDialogHasResumeAction && !selectedDialog?.registrationToken && !hasAiScreeningQuestions && (
									<p className="text-sm text-gray-400">Нет доступных ссылок</p>
								)}
							</div>
						</div>

					</div>
				</div>

				<div className={actionBarClass}>
					<button
						type="button"
						onClick={() => handleCandidateStatus('QUALIFIED')}
						className={`w-full text-white font-medium rounded-lg transition-colors bg-[#009966] hover:bg-[#008055] ${actionButtonClass}`}
					>
						Одобрить кандидата
					</button>
					<button
						type="button"
						onClick={() => handleCandidateStatus('REJECTED')}
						className={`w-full border border-red-600 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors ${actionButtonClass}`}
					>
						Отказать кандидату
					</button>
				</div>
			</div>
			</div>
			<EStaffResumeModal
				open={estaffResumeModalOpen}
				onClose={() => {
					setEstaffResumeModalOpen(false);
					setSelectedEStaffResume(null);
				}}
				resumeData={selectedEStaffResume}
			/>
		</div>
	);
};

export default DialogsTab;
